/* =========================================================
   Section: main-pod-wizard  (Phase 3)
   文件落点：assets/section-main-pod-wizard.js
   行为：
     - Phase 1: 5 步状态机 + 进度条同步
     - Phase 2: 变体自动反射 (findMatchingVariant) + 价格联动
     - Phase 3: R2 上传槽 + Send Later + 必填校验
     - Phase 4: Session 追踪 + Worker 上传集成
   数据依赖：
     - Shopify product JSON (options/variants/currency)
     - window.POD_CONFIG[sectionId].{uploadEndpoint, maxFileMB, publicUrlPrefix}
   ========================================================= */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
   * Utilities
   * ------------------------------------------------------------------ */

  /**
   * 生成或获取用户 Session ID
   * 使用 UUID v4 格式，存储在 localStorage 中
   * 用于追踪匿名用户的上传文件
   */
  function getOrCreateSessionId() {
    var key = 'pod_session_id';
    var id = localStorage.getItem(key);
    if (!id) {
      // 优先使用原生 crypto.randomUUID()
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        id = crypto.randomUUID();
      } else {
        // Fallback: 手动生成 UUID v4
        id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0;
          var v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }
      localStorage.setItem(key, id);
    }
    return id;
  }

  function readJSON(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return null; }
  }

  function formatMoney(cents, currency) {
    if (cents == null || isNaN(cents)) return '';
    var code = (currency && currency.iso_code) ? currency.iso_code : ((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ? window.Shopify.currency.active : 'USD');
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).format(cents / 100).replace(/\.00$/, '');
    } catch (e) {
      // 兼容极低版本浏览器的兜底方案
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    }
  }

  function escHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* findMatchingVariant(selections, variants, optionNames)
     仅当 selections 覆盖了全部 options 时才匹配（避免出现"假价"）。
     variants 由 Shopify 输出，键名为 option1/option2/option3（数字后缀）。
     这里把 optionNames 映射成数字 key，再做匹配。
  */
  function findMatchingVariant(selections, variants, optionNames) {
    if (!Array.isArray(optionNames) || optionNames.length === 0) return null;
    var missingKeys = optionNames.filter(function (n) {
      return selections[n] === undefined || selections[n] === null || selections[n] === '';
    });
    if (missingKeys.length > 0) return null;
    var keys = optionNames.map(function (_n, i) { return 'option' + (i + 1); });
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var ok = true;
      for (var k = 0; k < optionNames.length; k++) {
        var val = selections[optionNames[k]];
        var vk = v[keys[k]];
        if (vk !== val) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  /* DSN-9: HEIC/HEIF 自动检测 + 转换为 JPEG.

   * 流程:
   *   1. 文件选择或拖放后, 调用 _maybeConvertHeic(file)
   *   2. 检测通过 mime + 扩展名 + 魔术字节三重判定
   *   3. 是 HEIC: 动态加载 heic2any (jsDelivr CDN), 转 JPEG, 返回新 File 对象
   *   4. 否: 原样返回
   *   5. 失败 (库加载失败 / 转换异常): 抛出 onConvertError 回调, UI 提示用户
   *
   * heic2any 用法: heic2any({blob, toType:'image/jpeg', quality:0.92}) → Promise<Blob | Blob[]>
   * 单文件时返回 Blob, 多文件 (HEIF 多帧) 时返回数组, 我们只用第一个.
   *
   * 不强制依赖: 如果 CDN 失败, 让 _loadHeic2Any reject, UI 显示 fallback 错误.
   * 不打包进主 bundle: 主 wizard.js ~85KB, heic2any ~250KB, 动态加载避免首屏 cost.
   */
  var HEIC2ANY_CDN_URL = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';

  PodWizard.prototype._isHeicFile = function (file) {
    if (!file) return false;
    var name = (file.name || '').toLowerCase();
    var type = (file.type || '').toLowerCase();
    // 1) 扩展名 .heic / .heif
    if (/\.heic$|\.heif$/i.test(name)) return true;
    // 2) MIME 标头 image/heic / image/heif (iOS Safari 在某些版本会标)
    if (type === 'image/heic' || type === 'image/heif') return true;
    return false;
  };

  PodWizard.prototype._loadHeic2Any = function () {
    var self = this;
    if (self._heic2anyLib) return Promise.resolve(self._heic2anyLib);
    if (self._heic2anyLoading) return self._heic2anyLoading;

    self._heic2anyLoading = new Promise(function (resolve, reject) {
      // 已通过 <script> 加载?
      if (typeof window.heic2any === 'function') {
        self._heic2anyLib = window.heic2any;
        resolve(self._heic2anyLib);
        return;
      }
      var s = document.createElement('script');
      s.src = HEIC2ANY_CDN_URL;
      s.async = true;
      s.onload = function () {
        if (typeof window.heic2any === 'function') {
          self._heic2anyLib = window.heic2any;
          resolve(self._heic2anyLib);
        } else {
          reject(new Error('heic2any failed to register window.heic2any'));
        }
      };
      s.onerror = function () {
        reject(new Error('Failed to load heic2any from CDN: ' + HEIC2ANY_CDN_URL));
      };
      document.head.appendChild(s);
    }).catch(function (err) {
      // 单次失败后清 flag, 下次重新尝试
      self._heic2anyLoading = null;
      throw err;
    });

    return self._heic2anyLoading;
  };

  PodWizard.prototype._convertHeicToJpeg = function (file) {
    var self = this;
    return self._loadHeic2Any().then(function (lib) {
      return lib({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.92
      });
    }).then(function (result) {
      var blob = Array.isArray(result) ? result[0] : result;
      if (!blob || !(blob instanceof Blob)) {
        throw new Error('heic2any returned invalid blob');
      }
      var baseName = (file.name || 'image').replace(/\.(heic|heif)$/i, '');
      var newName = baseName + '.jpg';
      /* DSN-9: 构造 File 后 file.type='image/jpeg', 让后续严格白名单通过 */
      var newFile = new File([blob], newName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      console.log('[POD] HEIC converted to JPEG:', file.name, '→', newName, '(' + (newFile.size / 1024 / 1024).toFixed(2) + ' MB)');
      return newFile;
    });
  };

  PodWizard.prototype._maybeConvertHeic = function (file) {
    var self = this;
    if (!self._isHeicFile(file)) return Promise.resolve(file);
    /* 是 HEIC: 转 JPEG. 失败时 UI 端 (uploadFile) 已捕获 onConvertError. */
    return self._convertHeicToJpeg(file);
  };

  PodWizard.prototype._normalizeOptions = function (raw) {
    if (!Array.isArray(raw)) return [];
    return raw.map(function (opt, idx) {
      // Handle string-only array like ["尺寸","视觉类型"]
      if (typeof opt === 'string') {

        return { name: opt, values: [] };
      }
      var name = opt.name || opt.title || ('Option ' + (idx + 1));
      var values = [];
      if (Array.isArray(opt.values)) values = opt.values;
      else if (Array.isArray(opt.values_with_values)) values = opt.values_with_values.map(function (v) { return (typeof v === 'string') ? v : (v && v.name) ? v.name : String(v); });
      else if (Array.isArray(opt.options)) values = opt.options;
      else if (Array.isArray(opt.value)) values = opt.value;
      return { name: name, values: values };
    });
  };

  /* ------------------------------------------------------------------
   * Phase 3: Uploader (XHR + Worker signing)
   * ------------------------------------------------------------------ */

  function Uploader(config) {
    this.config = config || {};
    this.sessionId = getOrCreateSessionId();
  }

  /* requestSignedUrl(file)
     流程（真实上传模式，P3 迁移后）：
       1. POST uploadEndpoint + '/upload-sign' 拿签名 JSON {uploadUrl, cdnUrl, key, sessionId}
       2. PUT 文件 body 到 sign.uploadUrl（直传 R2）
       3. Worker 返回的 cdnUrl 即 publicUrl
       4. onprogress 回调上报 0-100
       5. resolve({ publicUrl, key, sessionId, size })
     回退路径：若 /upload-sign 不存在或失败 → 走 _uploadReal (multipart 旧路径)
     Mock 模式（endpoint 为空）：跳过签名，立即用 FileReader 读 dataURL 返回，
     适用于本地 / theme editor 调试。 publicUrl = dataURL，真实部署不可用。
  */
  Uploader.prototype.upload = function (file, callbacks) {
    callbacks = callbacks || {};
    var onProgress = callbacks.onProgress || function () {};
    var onError    = callbacks.onError    || function () {};

    if (!file || !/^image\//.test(file.type)) {
      onError(new Error('File must be an image.'));
      return;
    }
    var maxBytes = (this.config.maxFileMB || 10) * 1024 * 1024;
    if (file.size > maxBytes) {
      onError(new Error('File exceeds ' + (this.config.maxFileMB || 10) + ' MB.'));
      return;
    }

    var endpoint = this.config.uploadEndpoint;
    if (!endpoint) {
      this._uploadMock(file, onProgress);
      return;
    }
    // P3.2：默认走 presigned PUT（更快、更现代）
    this._uploadPresigned(endpoint, file, onProgress, onError);
  };

  Uploader.prototype._uploadMock = function (file, onProgress) {
    // 仿真进度 + dataURL 返回（保留 sessionId 用于后续处理）
    var self = this;
    var pct = 0;
    var tick = function () {
      pct = Math.min(100, pct + 20);
      onProgress(pct);
      if (pct < 100) setTimeout(tick, 30);
      else self._finishMock(file);
    };
    tick();
  };

  Uploader.prototype._finishMock = function (file) {
    // 立即解析，dataURL 作为 publicUrl 占位
    // 注意：Mock 模式下 sessionId 仍会传递给 _resolve，供后续流程使用
    var reader = new FileReader();
    reader.onload = function () {
      this._resolve({
        publicUrl: reader.result,
        key: 'mock-' + Date.now() + '-' + file.name,
        sessionId: this.sessionId,
        size: file.size
      });
    }.bind(this);
    reader.readAsDataURL(file);
  };

  /**
   * P3.2 真实上传到 Cloudflare Worker R2 — presigned PUT 模式（推荐）
   *   1. POST /upload-sign 拿签名
   *   2. PUT 文件到签名 URL（直传 R2，不走 Worker body）
   *   3. resolve(publicUrl, key, sessionId)
   *   - 比 multipart 更快：省一次网络跳转、不受 Worker 100MB 请求体限制
   *   - 失败 fallback 到 _uploadReal (multipart) 以保证向前兼容
   */
  Uploader.prototype._uploadPresigned = function (endpoint, file, onProgress, onError) {
    var uploader = this;
    var XHR = (typeof XMLHttpRequest !== 'undefined') ? XMLHttpRequest : window.XMLHttpRequest;

    var baseUrl = endpoint.replace(/\/upload\/?$/, '');
    var signEndpoint = baseUrl + '/upload-sign';

    var xhrSign = new XHR();
    xhrSign.open('POST', signEndpoint, true);
    xhrSign.setRequestHeader('Content-Type', 'application/json');
    xhrSign.onload = function () {
      if (xhrSign.status < 200 || xhrSign.status >= 300) {
        // 旧 worker 没 /upload-sign 路由（404）或签名失败 → fallback multipart
        console.warn('[Uploader] /upload-sign failed (status=' + xhrSign.status + '), falling back to multipart');
        uploader._uploadReal(endpoint, file, onProgress, onError);
        return;
      }
      var sign;
      try { sign = JSON.parse(xhrSign.responseText); }
      catch (e) {
        console.warn('[Uploader] /upload-sign invalid JSON, falling back to multipart');
        uploader._uploadReal(endpoint, file, onProgress, onError);
        return;
      }
      if (!sign.success || !sign.uploadUrl) {
        console.warn('[Uploader] /upload-sign returned no uploadUrl, falling back to multipart');
        uploader._uploadReal(endpoint, file, onProgress, onError);
        return;
      }

      // PUT 直传
      var xhrPut = new XHR();
      xhrPut.open('PUT', sign.uploadUrl, true);
      xhrPut.setRequestHeader('Content-Type', file.type);
      xhrPut.upload.onprogress = function (e) {
        if (e.lengthComputable) {
          onProgress(Math.round(e.loaded / e.total * 100));
        }
      };
      xhrPut.onload = function () {
        if (xhrPut.status < 200 || xhrPut.status >= 300) {
          /* 关键修复 (P2-3): PUT 失败时 fallback 到 multipart upload
           * 原代码: 直接 onError → 用户看到上传失败 → 实际可能是网络抖动 (PUT 到 R2 比 POST 到 Worker 更脆弱)
           * 现在: 退到 _uploadReal (multipart POST 到 Worker) */
          console.warn('[Uploader] PUT failed (status=' + xhrPut.status + '), falling back to multipart');
          uploader._uploadReal(endpoint, file, onProgress, onError);
          return;
        }
        onProgress(100);
        uploader._resolve({
          publicUrl: sign.cdnUrl,
          key: sign.key || '',
          sessionId: uploader.sessionId,
          size: file.size
        });
      };
      xhrPut.onerror = function () {
        /* 关键修复 (P2-3): PUT 网络错误也 fallback */
        console.warn('[Uploader] PUT network error, falling back to multipart');
        uploader._uploadReal(endpoint, file, onProgress, onError);
      };
      xhrPut.ontimeout = function () {
        /* 关键修复 (P2-3): PUT 超时也 fallback */
        console.warn('[Uploader] PUT timeout, falling back to multipart');
        uploader._uploadReal(endpoint, file, onProgress, onError);
      };
      xhrPut.send(file);
    };
    xhrSign.onerror = function () {
      console.warn('[Uploader] /upload-sign network error, falling back to multipart');
      uploader._uploadReal(endpoint, file, onProgress, onError);
    };
    xhrSign.send(JSON.stringify({
      filename: file.name,
      contentType: file.type,
      size: file.size,
      sessionId: uploader.sessionId
    }));
  };

  /**
   * 真实上传到 Cloudflare Worker R2 — multipart 旧路径（保留作为 fallback）
   * 使用 multipart/form-data 格式，直接 POST 到 Worker
   * Worker 端会处理文件存储并返回 CDN URL
   */
  Uploader.prototype._uploadReal = function (endpoint, file, onProgress, onError) {
    var uploader = this;
    var XHR = (typeof XMLHttpRequest !== 'undefined') ? XMLHttpRequest : window.XMLHttpRequest;
    var xhr = new XHR();

    // 确保 endpoint 包含 /upload 路径
    var uploadEndpoint = endpoint.replace(/\/upload\/?$/, '') + '/upload';
    xhr.open('POST', uploadEndpoint, true);

    // 使用 FormData 时不要设置 Content-Type，让浏览器自动处理
    var formData = new FormData();
    formData.append('file', file);
    formData.append('sessionId', this.sessionId);

    xhr.upload.onprogress = function (e) {
      if (e.lengthComputable) {
        onProgress(Math.round(e.loaded / e.total * 100));
      }
    };

    xhr.onload = function () {
      if (xhr.status < 200 || xhr.status >= 300) {
        onError(new Error('Upload failed: ' + xhr.status + ' ' + xhr.statusText));
        return;
      }

      var response;
      try {
        response = JSON.parse(xhr.responseText);
      } catch (e) {
        onError(new Error('Invalid JSON response from server'));
        return;
      }

      if (!response.success || !response.cdnUrl) {
        onError(new Error(response.error || 'Upload failed: no cdnUrl returned'));
        return;
      }

      onProgress(100);
      uploader._resolve({
        publicUrl: response.cdnUrl,
        key: response.key || '',
        sessionId: uploader.sessionId,
        size: file.size
      });
    };

    xhr.onerror = function () {
      onError(new Error('Network error during upload'));
    };

    xhr.send(formData);
  };

  Uploader.prototype._resolve = null; // 占位：实例方法，见下方 mountUploadSlots

  /* ------------------------------------------------------------------
   * PodWizard constructor
   * ------------------------------------------------------------------ */

  function PodWizard(root) {
    var self = this;
    this.root = root;
    this.sId = root.id;
    var _sectionId = root.dataset.sectionId || (root.id || '').replace(/^PodCustomizer-/, '');
    var configEl = document.getElementById('BundleConfigJson-' + _sectionId) || document.getElementById('BundleConfigJson-' + this.sId);
    this.bundleConfig = null;
    if (configEl && configEl.textContent && configEl.textContent.trim() !== 'null' && configEl.textContent.trim() !== '') {
      try { 
        var parsed = JSON.parse(configEl.textContent.trim()); 
        this.bundleConfig = (typeof parsed === 'string') ? JSON.parse(parsed) : parsed;
      } catch(e) { console.warn('BundleConfig parse error', e); }
    }
    var globalConfig = (window.POD_CONFIG && window.POD_CONFIG[this.sId]) || (window.POD_CONFIG && Object.values(window.POD_CONFIG)[0]) || {};
    this.config = {
      uploadEndpoint: root.dataset.podUploadWorkerEndpoint || globalConfig.uploadEndpoint || window.POD_UPLOAD_WORKER_ENDPOINT || '',
      workerEndpoint: root.dataset.podWorkerEndpoint || globalConfig.workerEndpoint || window.POD_WORKER_ENDPOINT || '',
      publicUrlPrefix: globalConfig.publicUrlPrefix || '',
      maxFileMB: Number(root.dataset.podMaxFileMb || globalConfig.maxFileMB || 10),
      /* 关键修复: 补充 visualTypeOptionNames 到 this.config（liquid 已注入到 POD_CONFIG）
         支持多个名称，逗号分隔，系统会自动匹配产品中存在的选项 */
      visualTypeOptionNames: (function() {
        var raw = globalConfig.visualTypeOptionNames || root.dataset.podVisualTypeOptions || '视觉类型';
        if (typeof raw === 'string') {
          return raw.split(',').map(function(s) { return s.trim(); }).filter(Boolean);
        }
        return Array.isArray(raw) ? raw : ['视觉类型'];
      })()
    };

    /* 变体 → 图片数映射表：
       来源：Shopify Admin → Products → 这个产品 → Metafields
         Namespace: pod
         Key: variant_upload_slots
         Type: JSON
         例: {"2变":2,"3变":3,"动画":1,"3D":1,"3D+动画":1}
       优先从 POD_CONFIG[sectionId].variantUploadSlots 读（已是对象则直接用），
       退回从 root.dataset.podVariantSlots 读（需要 JSON.parse），
       最后退回空对象（兜底用 max）。 */
    /* === FALLBACK 兜底默认值（当 metafield 未填时使用） === */
    var DEFAULT_VARIANT_UPLOAD_SLOTS = {
      '2变': 2,
      '3变': 3,
      '动画': 1,
      '3D': 1,
      '3D+动画': 1
    };

    var parsedMap = {};
    // POD_CONFIG.variantUploadSlots 通过 Liquid | json 输出，已是 JS 对象
    if (globalConfig.variantUploadSlots && typeof globalConfig.variantUploadSlots === 'object') {
      parsedMap = globalConfig.variantUploadSlots;
    } else {
      // 兜底：从 data 属性读取（需要解析）
      var rawVariantSlots = root.dataset.podVariantSlots || '';
      if (rawVariantSlots) {
        try { parsedMap = JSON.parse(rawVariantSlots) || {}; } catch (e) {
          console.warn('[POD Wizard] data-attr JSON parse failed, using fallback. raw=', rawVariantSlots);
          parsedMap = {};
        }
      }
    }
    /* 合并：代码兜底 < metafield（metafield 覆盖兜底） */
    if (!parsedMap || !Object.keys(parsedMap).length) {
      console.warn('[POD Wizard] No variantUploadSlots found in metafield/dataset — using DEFAULT_VARIANT_UPLOAD_SLOTS fallback.');
      parsedMap = DEFAULT_VARIANT_UPLOAD_SLOTS;
    }
    this.config.variantUploadSlots = (parsedMap && typeof parsedMap === 'object') ? parsedMap : {};
    console.log('[POD Wizard] variant slots map =', JSON.stringify(this.config.variantUploadSlots));

    this.steps = Array.prototype.slice.call(root.querySelectorAll('[data-step-index]'))
      .filter(function (el) { return el.classList.contains('pod-step'); })
      .sort(function (a, b) { return Number(a.dataset.stepIndex) - Number(b.dataset.stepIndex); });
    this.nodes = Array.prototype.slice.call(root.querySelectorAll('.pod-progress__node'));
    root.__podWizardInstance = this;
    this.current = Math.max(0, this.steps.findIndex(function (s) { return s.classList.contains('is-active'); }));
    if (this.current < 0) this.current = 0;

    this.product = readJSON(root, '[data-pod-product]') || {};
    console.log('[POD Wizard] ===== INITIALIZATION DEBUG =====');
    console.log('[POD Wizard] Raw product from data-pod-product:', JSON.stringify(this.product, null, 2));

    // pod-product-json.liquid outputs schemaVersion=2 format:
    // { schemaVersion: 2, options: [{name, position, values}], variants: [{id, options: [..], price, ...}] }
    // Legacy format (from {{ product | json }}): options_with_values or string array
    if (this.product.schemaVersion === 2) {
      console.log('[POD Wizard] Detected schemaVersion 2 format');
      this.options = (this.product.options || []).map(function(opt) {
        return { name: opt.name, values: opt.values || [] };
      });
      this.variants = (this.product.variants || []).map(function(v) {
        var copy = { id: v.id, price: v.price, available: v.available };
        // Extract option1/option2/option3 from the options array
        if (Array.isArray(v.options)) {
          copy.option1 = v.options[0];
          copy.option2 = v.options[1];
          copy.option3 = v.options[2];
        }
        return copy;
      });
    } else {
      console.log('[POD Wizard] Detected legacy format');
      // Legacy: options_with_values or string array
      this.options = this._normalizeOptions(this.product.options_with_values || this.product.options || []);
      this.variants = Array.isArray(this.product.variants) ? this.product.variants.map(function (v) {
        var copy = {};
        for (var k in v) if (Object.prototype.hasOwnProperty.call(v, k)) copy[k] = v[k];
        if (copy.option1 === undefined && copy.Option1 !== undefined) copy.option1 = copy.Option1;
        if (copy.option2 === undefined && copy.Option2 !== undefined) copy.option2 = copy.Option2;
        if (copy.option3 === undefined && copy.Option3 !== undefined) copy.option3 = copy.Option3;
        copy.price = (typeof copy.price === 'number') ? copy.price : (copy.price ? Number(copy.price) : null);
        copy.available = (copy.available === false) ? false : (copy.availableForSale === false ? false : true);
        return copy;
      }) : [];
    }

    console.log('[POD Wizard] processed this.options:', JSON.stringify(this.options, null, 2));
    console.log('[POD Wizard] processed variants count:', this.variants.length);
    this.meta = {
      slots: Number(root.dataset.podSlots || 1),
      slotsMax: (function () {
        /* 从 variantUploadSlots 里取最大值 + 兜底 */
        var map = self.config.variantUploadSlots || {};
        var max = Number(root.dataset.podSlots || 1);
        Object.keys(map).forEach(function (k) {
          var mapping = map[k];
          // 修复：兼容旧的数字格式和新的对象格式 { count: 3, hint: "..." }，避免 Number(obj) 变成 NaN
          var v = (typeof mapping === 'object' && mapping !== null) ? Number(mapping.count) : Number(mapping);
          if (isFinite(v) && v > max) max = v;
        });
        return max;
      })(),
      effectsEnabled: root.dataset.podEffectsEnabled === 'true',
      proofSlaHours: Number(root.dataset.podProofSlaHours || 24),
      approvalWindowHours: Number(root.dataset.podApprovalWindowHours || 72)
    };

    this.selections = {};
    this.currentVariant = null;
    this.slots = [];
    this.sendLater = false;
    this.uploadEnabled = root.dataset.podUploadEnabled !== 'false';
    this.uploader = new Uploader(this.config);
    var self = this;
    this.uploader._resolve = function (payload) { self._onUploadComplete(payload); };

    // DEBUG: Log initialization info
    console.log('[POD Wizard] ===== INITIALIZATION DEBUG =====');
    console.log('[POD Wizard] Raw product:', JSON.stringify(this.product, null, 2));
    console.log('[POD Wizard] steps count:', this.steps.length);
    this.steps.forEach(function(step, i) {
      console.log('[POD Wizard] step[' + i + ']: stepIndex=' + step.dataset.stepIndex + ', stepType=' + step.dataset.stepType + ', optionName=' + step.dataset.optionName + ', title=' + (step.querySelector('.pod-step__title') ? step.querySelector('.pod-step__title').textContent : 'N/A'));
    });
    console.log('[POD Wizard] progress nodes count:', this.nodes.length);
    this.nodes.forEach(function(node, i) {
      console.log('[POD Wizard] node[' + i + ']: stepIndex=' + node.dataset.stepIndex + ', stepType=' + node.dataset.stepType + ', label=' + node.querySelector('.pod-progress__label').textContent);
    });
    console.log('[POD Wizard] pod_upload_enabled (from data):', root.dataset.podUploadEnabled);
    console.log('[POD Wizard] ===== END DEBUG =====');

    this.bind();
    this.renderOptions();
    this.renderPriceBar();
    this.renderUploadSlots();
    this.refreshSubmitState();
    this.render();
    this.updateBackButton();

    // Auto-select first available option for each option group
    this.autoSelectFirstOptions();
  }

  PodWizard.prototype.autoSelectFirstOptions = function () {
    var self = this;
    console.log('[POD Wizard] autoSelectFirstOptions called, current options:', JSON.stringify(this.options.map(function(o){ return o.name; })));

    // Auto-select first visible option for each option (one per step)
    this.options.forEach(function (option) {
      var selectionKey = option.name;
      console.log('[POD Wizard] Checking option:', option.name, 'current selection:', this.selections[selectionKey]);
      if (!this.selections[selectionKey]) {
        // Find the container for this specific option
        var container = this.root.querySelector('[data-pod-options][data-option-name="' + option.name + '"]');
        console.log('[POD Wizard] Container for "' + option.name + '":', container ? 'FOUND' : 'NOT FOUND');
        if (!container) return;
        var firstPill = container.querySelector('.pod-pill:not([style*="display: none"]):not(.is-hidden):not(.is-disabled)');
        console.log('[POD Wizard] First pill found:', firstPill ? firstPill.dataset.value : 'NONE');
        if (firstPill) {
          var optValue = firstPill.dataset.value;
          if (optValue) {
            console.log('[POD Wizard] Auto-selecting:', option.name, '=', optValue);
            this.selectOption(option.name, optValue);
          }
        }
      }
    }, this);

    console.log('[POD Wizard] autoSelectFirstOptions completed, final selections:', JSON.stringify(this.selections));
  };

  /* ------------------------------------------------------------------
   * Event binding
   * ------------------------------------------------------------------ */

  PodWizard.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var next = e.target.closest('[data-pod-next]');
      var back = e.target.closest('[data-pod-back]');
      var pill = e.target.closest('[data-pod-pill]');
      var slotRemove = e.target.closest('[data-pod-slot-remove]');

      var log = '[POD Wizard] Click detected | target: ' + e.target.tagName + ' | className: ' + e.target.className + ' | next: ' + (next ? 'found' : 'null') + ' | current step: ' + self.current + ' | total steps: ' + self.steps.length + ' | isLastStep: ' + (self.current === self.steps.length - 1);
      console.log(log);
      sessionStorage.setItem('pod_debug', log);

      if (next) {
        e.preventDefault();
        var isLastStep = self.current === self.steps.length - 1;
        console.log('[POD Wizard] Next button clicked, isLastStep:', isLastStep);
        sessionStorage.setItem('pod_debug', sessionStorage.getItem('pod_debug') + ' | isLastStep: ' + isLastStep);
        if (isLastStep) {
          console.log('[POD Wizard] Calling handleAddToCart...');
          sessionStorage.setItem('pod_debug', sessionStorage.getItem('pod_debug') + ' | calling handleAddToCart');
          self.handleAddToCart();
        } else {
          self.tryGo(self.current + 1);
        }
      } else if (back) {
        e.preventDefault();
        self.go(self.current - 1);
      } else if (pill) {
        e.preventDefault();
        // Ignore disabled/out-of-stock pills
        if (pill.classList.contains('is-disabled') || pill.hasAttribute('data-out-of-stock')) {
          return;
        }
        var optionName = pill.dataset.option;
        var value = pill.dataset.value;
        if (optionName && value !== undefined) {
          self.selectOption(optionName, value);
        }

        // [核心修复]：读取 data-replace-img 并同步替换左侧主图
        var replaceImgUrl = pill.getAttribute('data-replace-img');
        if (replaceImgUrl) {
          // 找到页面的轮播图组件 (media-gallery)
          var gallery = document.querySelector('media-gallery');
          if (gallery) {
            // 优先找到当前正在显示的媒体块，如果没有就找第一个
            var activeMedia = gallery.querySelector('.is-active') || gallery.querySelector('[data-media-id]');
            if (activeMedia) {
              // 找到里面的 img 标签
              var img = activeMedia.querySelector('img');
              if (img) {
                // 覆盖 srcset 和 src，确保无论在任何屏幕尺寸下，都强制展示高清替换图
                img.setAttribute('srcset', replaceImgUrl);
                img.setAttribute('src', replaceImgUrl);
              }
            }
          }
        }
      } else if (slotRemove) {
        e.preventDefault();
        var slotIdx = Number(slotRemove.dataset.slotIndex);
        self.removeSlot(slotIdx);
      }
    });

    // Modal button events - bind to document level (modal is outside wizard root)
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-pod-modal-backdrop]') || e.target.closest('[data-pod-modal-close]') || e.target.matches('[data-pod-upload-modal]')) {
        // 点击遮罩或右上角 × 按钮：只关闭弹窗，不跳转
        self.hideUploadModal();
      } else if (e.target.closest('[data-pod-modal-skip]')) {
        // Skip = send later — allow Add to Cart without image
        self.sendLater = true;
        self._syncHiddenProperties();
        self.refreshSubmitState();
        var nextIdx = self.current + 1;
        self.hideUploadModal();
        self.go(nextIdx);
      } else if (e.target.closest('[data-pod-modal-upload]')) {
        // Upload now
        self.sendLater = false;
        self.hideUploadModal();
        var firstEmpty = self.root.querySelector('.pod-upload-slot[data-slot-status="idle"] [data-pod-file-input]');
        if (!firstEmpty) {
          firstEmpty = self.root.querySelector('.pod-upload-slot[data-slot-status="empty"] [data-pod-file-input]');
        }
        if (firstEmpty) firstEmpty.click();
      }
    });

    this.root.addEventListener('change', function (e) {
      if (e.target.matches('[data-pod-file-input]')) {
        var idx = Number(e.target.dataset.slotIndex);
        var file = e.target.files && e.target.files[0];
        if (file) self.handleFileSelect(idx, file);
      }
    });

    /* 拖拽上传 */
    this.root.addEventListener('dragover', function (e) {
      var dropZone = e.target.closest('[data-pod-dropzone]');
      if (!dropZone) return;
      e.preventDefault();
      dropZone.classList.add('is-dragover');
    });
    this.root.addEventListener('dragleave', function (e) {
      var dropZone = e.target.closest('[data-pod-dropzone]');
      if (!dropZone) return;
      dropZone.classList.remove('is-dragover');
    });
    this.root.addEventListener('drop', function (e) {
      var dropZone = e.target.closest('[data-pod-dropzone]');
      if (!dropZone) return;
      e.preventDefault();
      dropZone.classList.remove('is-dragover');
      var idx = Number(dropZone.dataset.slotIndex);
      var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) self.handleFileSelect(idx, file);
    });

    this.nodes.forEach(function (node) {
      node.addEventListener('click', function () {
        if (node.classList.contains('is-locked')) return;
        var idx = Number(node.dataset.stepIndex);
        if (node.classList.contains('is-complete') || node.classList.contains('is-active')) {
          self.tryGo(idx);
        }
      });
    });

    document.addEventListener('keydown', function (e) {
      if (!self.root.isConnected) return;
      if (e.key === 'ArrowRight') self.tryGo(self.current + 1);
      if (e.key === 'ArrowLeft') self.go(self.current - 1);
    });

    document.addEventListener('pod-pricebar:needs-render', function (e) {
      if (self.root.isConnected) {
        self.renderPriceBar();
      }
    });

    // 【核心修复】：接管数量加减点击事件，无缝触发价格刷新
    var qtyWrapper = self.root.querySelector('.pod-quantity');
    if (qtyWrapper) {
      qtyWrapper.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-pod-qty-btn]');
        if (!btn) return;
        e.preventDefault();
        var input = qtyWrapper.querySelector('input[name="quantity"]');
        var val = parseInt(input.value, 10) || 1;
        if (btn.dataset.podQtyBtn === 'minus' && val > 1) {
          input.value = val - 1;
        } else if (btn.dataset.podQtyBtn === 'plus') {
          input.value = val + 1;
        }
        self.renderPriceBar();
      });
      qtyWrapper.addEventListener('input', function(e) {
        if (e.target.name === 'quantity') {
          var val = parseInt(e.target.value, 10);
          if (isNaN(val) || val < 1) e.target.value = 1;
          self.renderPriceBar();
        }
      });
    }
  };

  /* ------------------------------------------------------------------
   * Step state machine (with Phase 3 canProceed guard)
   * ------------------------------------------------------------------ */

  PodWizard.prototype.tryGo = function (idx) {
    if (idx < 0 || idx >= this.steps.length) return;
    // If trying to leave the upload step without all slots filled, show modal
    if (this.steps[this.current] && this.steps[this.current].dataset.stepType === 'upload') {
      var allFilled = this.slots.every(function (s) { return s.status === 'done' && s.publicUrl; });
      if (!allFilled && !this.sendLater) {
        this.showUploadModal();
        return;
      }
    }
    this.go(idx);
  };

  PodWizard.prototype.showUploadModal = function () {
    var modal = document.querySelector('[data-pod-upload-modal]');
    if (modal) modal.removeAttribute('hidden');
  };

  PodWizard.prototype.hideUploadModal = function () {
    var modal = document.querySelector('[data-pod-upload-modal]');
    if (modal) modal.setAttribute('hidden', '');
  };

  PodWizard.prototype.canProceed = function (stepIndex) {
    // No per-step validation - Crown & Paw style: user can freely navigate
    // This function is kept for compatibility but returns true always
    return true;
  };

  PodWizard.prototype.validateForSubmit = function () {
    // Only validate when submitting to cart
    // Upload step: all slots must be filled
    var allFilled = this.slots.every(function (s) { return s.status === 'done' && s.publicUrl; });
    if (!allFilled) {
      this.showUploadError('Please upload all required photos before submitting.');
      return false;
    }
    // No validation needed for options - Crown & Paw allows checkout without selecting
    this.showUploadError(null);
    return true;
  };

  PodWizard.prototype.go = function (idx) {
    console.log('[POD Wizard] go called with idx:', idx);
    if (idx < 0 || idx >= this.steps.length) {
      console.log('[POD Wizard] go: invalid index');
      return;
    }
    this.current = idx;
    console.log('[POD Wizard] go: set current to', idx);
    this.render();
    this.updateBackButton();
  };

  PodWizard.prototype.updateBackButton = function () {
    var currentStep = this.steps[this.current];
    if (!currentStep) return;
    var backBtn = currentStep.querySelector('[data-pod-back]');
    if (backBtn) {
      if (this.current === 0) {
        // 第一步绝对不显示返回按钮
        backBtn.style.display = 'none';
        backBtn.setAttribute('hidden', '');
        backBtn.classList.remove('is-visible');
      } else {
        // 非第一步才正常显示
        backBtn.style.display = '';
        backBtn.removeAttribute('hidden');
        backBtn.classList.add('is-visible');
      }
    }
  };


  PodWizard.prototype.render = function () {
    var self = this;
    var container = this.root.querySelector('.pod-steps');
    
    // 内部函数：集中处理状态切换
    function updateDOM() {
      self.steps.forEach(function (step, i) {
        step.classList.toggle('is-active', i === self.current);
      });
      self.nodes.forEach(function (node, i) {
        node.classList.remove('is-active', 'is-complete', 'is-locked');
        if (i < self.current) node.classList.add('is-complete');
        else if (i === self.current) node.classList.add('is-active');
        else node.classList.add('is-locked');
      });
      self.updateNextButton();
    }

    if (!container) {
      updateDOM();
      return;
    }

    // 阶段 1：记录当前高度并锁定
    var oldHeight = container.offsetHeight;
    container.style.height = oldHeight + 'px';
    container.style.overflow = 'hidden';
    container.style.transition = 'height 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)';
    container.offsetHeight; // 强制重排，应用锁定状态

    // 阶段 2：瞬间切换 DOM 状态
    updateDOM();

    // 阶段 3：预判新内容的真实高度
    container.style.height = 'auto';
    var newHeight = container.offsetHeight;
    
    // 恢复旧高度，准备启动动画
    container.style.height = oldHeight + 'px';
    container.offsetHeight; // 强制重排

    // 阶段 4：执行高度平滑过渡动画
    container.style.height = newHeight + 'px';

    // 同步执行平滑滚动对齐（跳过首次页面加载时的初始化）
    if (self._isRendered) {
      var targetOffset = self.root.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({
        top: targetOffset,
        behavior: 'smooth'
      });
    }
    self._isRendered = true;

    // 阶段 5：动画结束后（300ms）清理内联样式，交还给自然文档流
    setTimeout(function() {
      container.style.height = '';
      container.style.overflow = '';
      container.style.transition = '';
    }, 300);
  };

  PodWizard.prototype.updateNextButton = function () {
    var currentStep = this.steps[this.current];
    if (!currentStep) return;
    var nextBtn = currentStep.querySelector('[data-pod-next]');
    if (!nextBtn) return;

    var isLastStep = this.current === this.steps.length - 1;
    var textSpan = nextBtn.querySelector('[data-pod-next-text]');

    if (isLastStep) {
      if (textSpan) {
        textSpan.textContent = 'Add to Cart';
      } else {
        nextBtn.textContent = 'Add to Cart';
      }
      nextBtn.setAttribute('aria-label', 'Add to Cart');
      nextBtn.classList.add('pod-btn--submit');
    } else {
      if (textSpan) {
        textSpan.textContent = 'Next';
      } else {
        nextBtn.textContent = 'Next';
      }
      nextBtn.setAttribute('aria-label', 'Next step');
      nextBtn.classList.remove('pod-btn--submit');
    }
  };

  /* ------------------------------------------------------------------
   * Phase 2: Options rendering & price binding
   * ------------------------------------------------------------------ */

  /* _getOptionPriceDiff(optionIndex, value)
     计算当前选项值相比前一步的差价。
     - 第一个选项：相对于最低价 variant
     - 后续选项：相对于前一个已选选项的最低价
     返回差价值（cents），如果无法计算则返回 null。
  */
  PodWizard.prototype._getOptionPriceDiff = function (optionIndex, value) {
    var self = this;
    var currency = this.product.currency || this.product.price_currency || null;

    // 找到这个选项值对应的所有 variants
    var matchingVariants = this.variants.filter(function (v) {
      var key = 'option' + (optionIndex + 1);
      return v[key] === value && v.available !== false;
    });

    if (matchingVariants.length === 0) return null;

    // 找到这些 variants 中的最低价
    var minPrice = Math.min.apply(null, matchingVariants.map(function (v) { return v.price; }));

    if (optionIndex === 0) {
      // 第一个选项：相对于所有 variants 的最低价
      var globalMinPrice = Math.min.apply(null, this.variants.filter(function (v) { return v.available !== false; }).map(function (v) { return v.price; }));
      if (globalMinPrice === minPrice) return 0;
      return minPrice - globalMinPrice;
    } else {
      // 后续选项：相对于前一个已选选项的值
      var prevOption = this.options[optionIndex - 1];
      var prevSelection = this.selections[prevOption.name];
      if (!prevSelection) return null;

      // 找到前一个选项值对应的最低价
      var prevKey = 'option' + optionIndex;
      var prevVariants = this.variants.filter(function (v) {
        return v[prevKey] === prevSelection && v.available !== false;
      });
      if (prevVariants.length === 0) return null;

      var prevMinPrice = Math.min.apply(null, prevVariants.map(function (v) { return v.price; }));
      if (minPrice === prevMinPrice) return 0;
      return minPrice - prevMinPrice;
    }
  };

  /* _formatMoney(cents, currency) - instance wrapper for formatMoney */
  PodWizard.prototype._formatMoney = function (cents, currency) {
    return formatMoney(cents, currency);
  };

  PodWizard.prototype.renderOptions = function () {
    var self = this;
    var optionContainers = this.root.querySelectorAll('[data-pod-options]');
    console.log('[POD Wizard] renderOptions called, containers found:', optionContainers.length);

    if (optionContainers.length === 0) {
      console.log('[POD Wizard] No option containers found!');
      return;
    }

    // If Liquid already server-rendered option content, just update availability
    var hasContent = false;
    optionContainers.forEach(function (container) {
      if (container.querySelectorAll('.pod-options__group').length > 0) {
        hasContent = true;
      }
    });

    console.log('[POD Wizard] hasContent from server:', hasContent);

    if (hasContent) {
      console.log('[POD Wizard] Skipping JS render, using server-rendered content');
      this.updateOptionVisibility();
      return;
    }

    // Render from scratch - put each option in its corresponding step container
    console.log('[POD Wizard] Rendering options from JS...');
    this.options.forEach(function (option, optionIndex) {
      console.log('[POD Wizard] Looking for container for option[' + optionIndex + ']:', option.name);
      // Find the container for this option index
      var container = self.root.querySelector('[data-pod-options][data-option-index="' + optionIndex + '"]');
      console.log('[POD Wizard] Container found:', container ? 'YES' : 'NO');
      if (container) {
        console.log('[POD Wizard] Rendering option "' + option.name + '" to container with data-option-index="' + optionIndex + '"');
        self._renderOptionGroup(container, option);
      } else {
        // Fallback: try to find by option-name
        var containerByName = self.root.querySelector('[data-pod-options][data-option-name="' + option.name + '"]');
        console.log('[POD Wizard] Fallback container by name found:', containerByName ? 'YES' : 'NO');
        if (containerByName) {
          self._renderOptionGroup(containerByName, option);
        }
      }
    });
  };

  PodWizard.prototype._renderOptionGroup = function (container, option) {
    var self = this;
    var optionIndex = this.options.indexOf(option);
    var fields = document.createElement('fieldset');
    fields.className = 'pod-options__group';
    fields.dataset.option = option.name;
    var legend = document.createElement('legend');
    legend.className = 'pod-options__label caption';
    legend.textContent = option.name;
    fields.appendChild(legend);

    var list = document.createElement('div');
    list.className = 'pod-options__list';
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-label', option.name);

    // For the first option, show all values that have stock in at least one variant
    // For subsequent options, we need to show values that could be available given previous selections
    var showAllWithStock = (optionIndex === 0);

    (option.values || []).forEach(function (v, imgIndex) {
      var valStr = (typeof v === 'string') ? v : (v && v.name) ? v.name : String(v);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pod-pill';
      btn.dataset.option = option.name;
      btn.dataset.value = valStr;
      btn.dataset.podPill = '';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');

      // 仅当图片真实存在时，才注入图片容器和特殊 class
      var productImg = self.product.images && self.product.images[imgIndex];
      if (productImg && productImg.src) {
        btn.classList.add('pod-pill--image');
        var imgWrap = document.createElement('span');
        imgWrap.className = 'pod-pill__img-wrap';
        var img = document.createElement('img');
        img.src = productImg.src;
        img.alt = valStr;
        img.className = 'pod-pill__img';
        img.loading = 'lazy';
        imgWrap.appendChild(img);
        btn.appendChild(imgWrap);
      }

      // Check if this option value has stock (considering previous selections if any)
      var hasStock = self.variants.some(function (variant) {
        var variantOptionKey = 'option' + (optionIndex + 1);
        var variantOptionValue = variant[variantOptionKey];
        if (variantOptionValue !== valStr) return false;
        if (variant.available !== true) return false;

        // Check if all previous options are selected and compatible
        for (var prevIdx = 0; prevIdx < optionIndex; prevIdx++) {
          var prevOption = self.options[prevIdx];
          var prevSelection = self.selections[prevOption.name];
          var prevVariantKey = 'option' + (prevIdx + 1);
          if (prevSelection && variant[prevVariantKey] !== prevSelection) {
            return false;
          }
        }
        return true;
      });

      // For non-first options, always show pills but mark as disabled/hidden based on current selection
      if (!showAllWithStock && !hasStock) {
        // Check if this value exists in any variant at all (for later availability updates)
        var existsInVariants = self.variants.some(function (variant) {
          var variantOptionKey = 'option' + (optionIndex + 1);
          return variant[variantOptionKey] === valStr && variant.available === true;
        });
        if (!existsInVariants) {
          return; // Skip this value entirely
        }
      }

      var txt = document.createElement('span');
      txt.className = 'pod-pill__label';
      txt.textContent = valStr;
      btn.appendChild(txt);

      // Always add price diff placeholder (calculation handled by updatePriceDiffs)
      var diffBadge = document.createElement('span');
      diffBadge.className = 'pod-pill__price-diff';
      btn.appendChild(diffBadge);

      list.appendChild(btn);
    });

    // If no options available in this group, hide the entire group
    if (list.children.length === 0) {
      fields.style.display = 'none';
    }

    fields.appendChild(list);
    container.appendChild(fields);
  };

  PodWizard.prototype.selectOption = function (optionName, value) {
    console.log('[POD Wizard] selectOption called:', optionName, '=', value);
    this.selections[optionName] = value;
    this.root.querySelectorAll('[data-option="' + optionName + '"]').forEach(function (el) {
      if (el.classList.contains('pod-pill')) {
        var active = el.dataset.value === value;
        el.classList.toggle('is-selected', active);
        el.setAttribute('aria-checked', active ? 'true' : 'false');
      }
    });
    var selectedSlot = this.root.querySelector('[data-selected-for="' + optionName + '"]');
    if (selectedSlot) selectedSlot.textContent = ': ' + value;
    this.recomputeVariant();
    this.renderPriceBar();
    this.refreshSubmitState();

    // Tell pod-extras-item.js the base variant price changed
    if (this.currentVariant && typeof this.currentVariant.price === 'number') {
      document.dispatchEvent(new CustomEvent('pod-variant:change', {
        detail: { priceCents: this.currentVariant.price }
      }));
    }

    // Update ALL option containers (not just current step's container)
    this.updateAllOptionAvailability();

    // Update price diff badges for subsequent options
    this.updatePriceDiffs();

    /* 变体决定激活的 slot 数: 用户切换 "视觉类型" option 时, 把多余 slot 隐藏
     * 关键修复: 支持多个名称（逗号分隔），遍历找第一个匹配的选项
     */
    var optionNames = (this.config && this.config.visualTypeOptionNames) || ['视觉类型'];
    var matchedOpt = null;
    // 遍历配置的名称列表，找第一个产品中存在的选项
    for (var i = 0; i < optionNames.length; i++) {
      var optName = optionNames[i];
      for (var j = 0; j < this.options.length; j++) {
        if (this.options[j].name === optName) {
          matchedOpt = optName;
          break;
        }
      }
      if (matchedOpt) break;
    }
    // 如果配置的名称都没匹配上，fallback 到自动检测
    if (!matchedOpt) {
      for (var k = 0; k < this.options.length; k++) {
        var n = this.options[k].name || '';
        if (/视觉|visual|style|effect/i.test(n)) {
          matchedOpt = n;
          break;
        }
      }
    }
    if (optionName === matchedOpt) {
      this._applyActiveSlotCount();
    }

    // Auto-advance to next step after selection (if not the last step and not in inline mode)
    var currentStep = this.steps[this.current];
    var isInlineMode = this.root.dataset.podMode === 'inline';
    console.log('[POD Wizard] current step:', this.current, 'type:', currentStep ? currentStep.dataset.stepType : 'N/A', 'inlineMode:', isInlineMode);
    if (currentStep && currentStep.dataset.stepType === 'option' && !isInlineMode) {
      // Find the index of this option
      var optionIndex = -1;
      for (var i = 0; i < this.options.length; i++) {
        if (this.options[i].name === optionName) {
          optionIndex = i;
          break;
        }
      }
      console.log('[POD Wizard] optionIndex:', optionIndex, 'options.length:', this.options.length);
      // If this is the last option step, don't auto-advance
      var nextStepIndex = this.current + 1;
      console.log('[POD Wizard] nextStepIndex:', nextStepIndex, 'steps.length:', this.steps.length);
      if (nextStepIndex < this.steps.length) {
        var nextStep = this.steps[nextStepIndex];
        console.log('[POD Wizard] nextStep type:', nextStep ? nextStep.dataset.stepType : 'N/A');
        if (nextStep && nextStep.dataset.stepType === 'option') {
          // Auto-advance to next option step after a short delay
          var self = this;
          console.log('[POD Wizard] Auto-advancing to step:', nextStepIndex);
          setTimeout(function() {
            self.go(nextStepIndex);
          }, 150);
        }
      }
    }
  };

  // Update price diff badges using "Control Variable Analysis" algorithm
  // For each option group:
  //   Step A: Lock all other options to current selections (or first available if not selected)
  //   Step B: Find the minimum price among all combinations with this option's values (group_baseline_price)
  //   Step C: Calculate diff = variant_price - group_baseline_price for each value
  PodWizard.prototype.updatePriceDiffs = function () {
    var self = this;
    var currency = this.product.currency || this.product.price_currency || null;

    console.log('[POD Wizard] updatePriceDiffs called, current selections:', JSON.stringify(this.selections));

    // Iterate through each option group
    this.options.forEach(function (option, optIdx) {
      var container = self.root.querySelector('[data-pod-options][data-option-index="' + optIdx + '"]');
      if (!container) {
        console.log('[POD Wizard] updatePriceDiffs: container NOT found for optIdx=' + optIdx);
        return;
      }

      var list = container.querySelector('.pod-options__list');
      if (!list) {
        console.log('[POD Wizard] updatePriceDiffs: list NOT found for optIdx=' + optIdx);
        return;
      }

      var pills = list.querySelectorAll('.pod-pill');
      console.log('[POD Wizard] updatePriceDiffs: optIdx=' + optIdx + ', option=' + option.name + ', pills count=' + pills.length);
      if (pills.length === 0) return;

      // Step A: Build locked selections (other options)
      // If an option is not yet selected, use its first available value
      var lockedSelections = {};
      var allLocked = true;
      self.options.forEach(function (otherOpt, otherIdx) {
        if (otherIdx !== optIdx) {
          var selected = self.selections[otherOpt.name];
          if (!selected) {
            // Try to find first available value for this option
            var firstAvailable = null;
            (otherOpt.values || []).forEach(function (val) {
              if (firstAvailable) return;
              var valStr = typeof val === 'string' ? val : (val && val.name) ? val.name : String(val);
              var hasVariant = self.variants.some(function (v) {
                if (v.available === false) return false;
                var optKey = 'option' + (otherIdx + 1);
                if (v[optKey] !== valStr) return false;
                return true;
              });
              if (hasVariant) firstAvailable = valStr;
            });
            if (firstAvailable) {
              selected = firstAvailable;
            } else {
              allLocked = false;
            }
          }
          if (selected) {
            lockedSelections['option' + (otherIdx + 1)] = selected;
          }
        }
      });

      // If we couldn't lock all other options, skip this iteration
      if (!allLocked) return;

      // Step B: Find group_baseline_price (minimum price among all valid combinations)
      var groupBaselinePrice = Infinity;

      pills.forEach(function (pill) {
        var valStr = pill.dataset.value;
        var optKey = 'option' + (optIdx + 1);

        // Find minimum price for this value with locked other options
        var matchingVariants = self.variants.filter(function (v) {
          if (v.available === false) return false;
          if (v[optKey] !== valStr) return false;
          // Check all locked selections
          for (var key in lockedSelections) {
            if (v[key] !== lockedSelections[key]) return false;
          }
          return true;
        });

        if (matchingVariants.length > 0) {
          var minPrice = Math.min.apply(null, matchingVariants.map(function (v) { return v.price; }));
          if (minPrice < groupBaselinePrice) {
            groupBaselinePrice = minPrice;
          }
        }
      });

      // If no valid baseline found, clear all badges and return
      if (groupBaselinePrice === Infinity) {
        pills.forEach(function (pill) {
          var badge = pill.querySelector('.pod-pill__price-diff');
          if (badge) badge.remove();
          pill.classList.remove('has-price-diff');
        });
        return;
      }

      // Step C: Calculate and render diff for each pill
      pills.forEach(function (pill) {
        var valStr = pill.dataset.value;
        var optKey = 'option' + (optIdx + 1);

        // Find price for this specific value with locked other options
        var matchingVariants = self.variants.filter(function (v) {
          if (v.available === false) return false;
          if (v[optKey] !== valStr) return false;
          for (var key in lockedSelections) {
            if (v[key] !== lockedSelections[key]) return false;
          }
          return true;
        });

        var badge = pill.querySelector('.pod-pill__price-diff');
        var variantPrice;

        if (matchingVariants.length > 0) {
          variantPrice = Math.min.apply(null, matchingVariants.map(function (v) { return v.price; }));
          var diff = variantPrice - groupBaselinePrice;

          if (diff > 0) {
            if (!badge) {
              badge = document.createElement('span');
              badge.className = 'pod-pill__price-diff';
              pill.appendChild(badge);
            }
            var priceString = '+' + formatMoney(diff, currency);
            priceString = priceString.replace(/\.00$/, '').replace(/(\.\d*?[1-9])0+$/, '$1');
            badge.textContent = priceString;
            badge.className = 'pod-pill__price-diff is-positive';
            pill.classList.add('has-price-diff');
          } else {
            if (badge) {
              badge.textContent = '';
              badge.className = 'pod-pill__price-diff';
            }
            pill.classList.remove('has-price-diff');
          }
        } else {
          // No valid variant for this combination - mark as unavailable
          if (badge) badge.remove();
          pill.classList.remove('has-price-diff');
          pill.classList.add('is-unavailable');
        }
      });
    });
  };

  // Update availability for ALL option containers based on current selections
  PodWizard.prototype.updateAllOptionAvailability = function () {
    var self = this;
    var optionContainers = this.root.querySelectorAll('[data-pod-options]');

    optionContainers.forEach(function (container) {
      var fieldsets = container.querySelectorAll('.pod-options__group');
      fieldsets.forEach(function (fieldset) {
        var fieldsetOptionName = fieldset.dataset.option;
        if (!fieldsetOptionName) return;

        var optIdx = -1;
        for (var i = 0; i < self.options.length; i++) {
          if (self.options[i].name === fieldsetOptionName) {
            optIdx = i;
            break;
          }
        }
        if (optIdx === -1) return;

        var list = fieldset.querySelector('.pod-options__list');
        if (!list) return;

        var pills = list.querySelectorAll('.pod-pill');

        pills.forEach(function (pill) {
          var valStr = pill.dataset.value;
          var sizesWithStock = pill.dataset.sizesWithStock;

          // For non-first options - check if current selections make this option available
          var hasAvailableVariants = self.variants.some(function (variant) {
            var variantOptionKey = 'option' + (optIdx + 1);
            var variantOptionValue = variant[variantOptionKey];
            if (variantOptionValue !== valStr) return false;
            if (variant.available !== true) return false;

            // Check if all previous options are selected and compatible
            for (var prevIdx = 0; prevIdx < optIdx; prevIdx++) {
              var prevOption = self.options[prevIdx];
              var prevSelection = self.selections[prevOption.name];
              var prevVariantKey = 'option' + (prevIdx + 1);
              if (prevSelection && variant[prevVariantKey] !== prevSelection) {
                return false;
              }
            }
            return true;
          });

          if (hasAvailableVariants) {
            pill.style.display = '';
          } else {
            pill.style.display = 'none';
          }
        });
      });
    });
  };

  // Hide/show pills based on current stock (for Liquid-rendered content)
  PodWizard.prototype.updateOptionVisibility = function () {
    var self = this;
    var optionContainers = this.root.querySelectorAll('[data-pod-options]');

    optionContainers.forEach(function (container) {
      var fieldsets = container.querySelectorAll('.pod-options__group');
      fieldsets.forEach(function (fieldset) {
        var optionName = fieldset.dataset.option;
        var optionIndex = -1;
        for (var i = 0; i < self.options.length; i++) {
          if (self.options[i].name === optionName) {
            optionIndex = i;
            break;
          }
        }
        if (optionIndex === -1) return;

        var pills = fieldset.querySelectorAll('.pod-pill');
        var visibleCount = 0;

        pills.forEach(function (pill) {
          var valStr = pill.dataset.value;

          // Check if this option value has stock (considering previous selections)
          var hasStock = self.variants.some(function (variant) {
            var variantOptionKey = 'option' + (optionIndex + 1);
            var variantOptionValue = variant[variantOptionKey];
            if (variantOptionValue !== valStr) return false;
            if (variant.available !== true) return false;

            // For options after the first, check if previous options make this available
            for (var prevIdx = 0; prevIdx < optionIndex; prevIdx++) {
              var prevOption = self.options[prevIdx];
              var prevSelection = self.selections[prevOption.name];
              var prevVariantKey = 'option' + (prevIdx + 1);
              if (prevSelection && variant[prevVariantKey] !== prevSelection) {
                return false;
              }
            }
            return true;
          });

          if (hasStock) {
            pill.style.display = '';
            visibleCount++;
          } else {
            pill.style.display = 'none';
          }
        });

        // Hide entire fieldset if no visible options
        fieldset.style.display = visibleCount > 0 ? '' : 'none';
      });
    });
  };

  PodWizard.prototype.updateOptionAvailability = function () {
    // Now handled by updateAllOptionAvailability
    this.updateAllOptionAvailability();
  };

  PodWizard.prototype.recomputeVariant = function () {
    if (!this.variants.length) { this.currentVariant = null; return; }
    var optionNames = this.options.map(function (o) { return o.name; });
    this.currentVariant = findMatchingVariant(this.selections, this.variants, optionNames);
  };

  PodWizard.prototype.renderPriceBar = function () {
    var totalEl = this.root.querySelector('[data-pod-total-price]');
    var variant = this.currentVariant;
    var currency = this.product.currency || this.product.price_currency || (window.POD_CONFIG && window.POD_CONFIG[this.sId] && window.POD_CONFIG[this.sId].productPriceCurrency) || null;

    var configPrice = (window.POD_CONFIG && window.POD_CONFIG[this.sId] && window.POD_CONFIG[this.sId].productPrice) || null;

    if (!variant || typeof variant.price !== 'number') {
      if (totalEl && configPrice != null) {
        var m = formatMoney(configPrice, currency);
        if (totalEl) totalEl.innerHTML = m;
      } else if (totalEl && totalEl.textContent && totalEl.textContent !== '—') {
        // leave server-rendered value as-is
      } else {
        if (totalEl) totalEl.textContent = '—';
      }
      return;
    }

    var baseCents = variant.price;
    var baseOriginalCents = (typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price) ? variant.compare_at_price : variant.price;

    var extrasCents = 0;
    var extrasOriginalCents = 0;

    if (window.POD_EXTRAS_STATE && window.POD_EXTRAS_STATE.selectedExtras) {
      window.POD_EXTRAS_STATE.selectedExtras.forEach(function(x) {
        extrasCents += (x.priceCents || 0);
        extrasOriginalCents += (x.originalPriceCents || x.priceCents || 0);
      });
    }

    // 【核心修复】：实时读取数量框的值
    var qtyInput = this.root.querySelector('input[name="quantity"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    // 解析阶梯折扣 (匹配规则: 从头遍历，只要当前数量大于等于配置的阶梯数量，就覆盖应用，最终拿到最高符合阶梯)
    var discountPct = 0;
    if (this.bundleConfig && this.bundleConfig.tiers && this.bundleConfig.tiers.length > 0) {
      var tiers = this.bundleConfig.tiers;
      var activeTier = tiers[0];
      for (var i = 0; i < tiers.length; i++) {
        if (qty >= tiers[i].qty) {
          activeTier = tiers[i];
        }
      }
      discountPct = activeTier.discount_pct || 0;
    }

    // 主商品应用折扣
    var discountedBaseCents = Math.round(baseCents * (1 - discountPct / 100));

    // 总价 = (折后主商品 + 附加商品) * 数量
    var totalCurrentCents = (discountedBaseCents + extrasCents) * qty;
    
    // 强制获取真实的划线原价（如果变体没有配置 compare_at_price，则原价即为本身售价）
    var trueBaseOriginal = (baseOriginalCents > baseCents) ? baseOriginalCents : baseCents;
    var totalOriginalCents = (trueBaseOriginal + extrasOriginalCents) * qty;

    var html = '';
    // 1. 实付款在前，放大到 2.6rem
    html += '<span class="pod-price-final" style="font-size: 2.6rem; font-weight: 700; color: rgb(var(--color-foreground)); line-height: 1;">' + formatMoney(totalCurrentCents, currency) + '</span>';

    // 2. 划线价在后，字号 1.6rem
    if (totalOriginalCents > totalCurrentCents) {
      html += '<s class="pod-pricebar__compare-price-js" style="color: rgba(var(--color-foreground), 0.5); font-size: 1.6rem; font-weight: 400; text-decoration: line-through; margin-left: 10px;">' + formatMoney(totalOriginalCents, currency) + '</s>';
    }
    
    // 3. 标签 1：主商品配置的固定阶梯折扣
    if (discountPct > 0) {
      html += '<span class="pod-summary-badge pod-summary-badge--main">SAVE ' + discountPct + '%</span>';
    }

    // 4. 标签 2：附加项专属优惠
    if (extrasOriginalCents > extrasCents) {
      html += '<span class="pod-summary-badge pod-summary-badge--extra">EXTRAS SAVINGS</span>';
    }

    if (totalEl) {
      totalEl.innerHTML = html;
      totalEl.style.display = 'flex';
      totalEl.style.alignItems = 'center';
    }
  };

  /* ------------------------------------------------------------------
   * Phase 3: Upload slots
   * ------------------------------------------------------------------ */

  PodWizard.prototype.renderUploadSlots = function () {
    var self = this;
    var container = this.root.querySelector('[data-pod-upload-slots]');
    if (!container) return;

    /* 先按 slotsMax 渲染（兜底：哪怕没 metafield，至少能渲染出最大数量的 slot） */
    var slotCount = this.meta.slotsMax || this.meta.slots || 1;
    this.slots = [];
    container.innerHTML = '';
    for (var i = 0; i < slotCount; i++) {
      this.slots.push({ status: 'idle', file: null, previewUrl: null, publicUrl: null, progress: 0 });
      container.appendChild(this._buildSlot(i));
    }

    /* 动态设置 CSS 变量 --pod-slot-cols：决定 grid 列数
       - 1 个 → 1 列
       - 2-3 个 → 1 行（按 slot 数量）
       - 4 个 → 2 列
       - 5+ → 3 列上限（CSS 已兜底 max 3） */
    /* 初始 cols 等于最大数；后面 _applyActiveSlotCount 会根据当前选项覆盖 */
    var initialCols = Math.min(slotCount, 3);
    container.style.setProperty('--pod-slot-cols', String(initialCols));

    /* 渲染隐藏 properties inputs（Add to Cart 提交到 Shopify 时携带） */
    this._renderHiddenProperties();

    /* 然后根据当前选中的「视觉类型」决定激活几张 */
    /* （不销毁 slot，只是把多余的 slot 标 hidden + 移除属性） */
    this._applyActiveSlotCount();

    /* 页面加载时：从 R2 恢复该 sessionId 下之前上传但未下单的文件 */
    /* 用 requestIdleCallback 包一层：把这次网络请求推到浏览器空闲时段，
       不抢 LCP（LCP 是产品主图，不是上传预览图），对广告投放无影响 */
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(function () { self._restoreFromR2(); }, { timeout: 2000 });
    } else {
      setTimeout(function () { self._restoreFromR2(); }, 50);
    }
  };

  /* ------------------------------------------------------------------
   * _requiredSlotCount — 根据当前选中的「视觉类型」返回需要上传几张图
   *   优先看 variantUploadSlots 映射表里的值；
   *   没有映射值时退回 meta.slotsMax。
   * ------------------------------------------------------------------ */
  PodWizard.prototype._requiredSlotCount = function () {
    var map = (this.config && this.config.variantUploadSlots) || {};
    console.log('[POD Wizard] _requiredSlotCount: map =', JSON.stringify(map));
    /* 关键修复: 支持多个名称（逗号分隔），遍历找第一个产品中存在的选项 */
    var visualType = null;
    var optionNames = (this.config && this.config.visualTypeOptionNames) || ['视觉类型'];
    if (this.selections) {
      console.log('[POD Wizard] _requiredSlotCount: searching selections for visual type, optionNames =', JSON.stringify(optionNames), 'selections =', JSON.stringify(this.selections));
      // 遍历配置的名称列表，找第一个产品中存在的选项
      for (var i = 0; i < optionNames.length; i++) {
        var optName = optionNames[i];
        if (this.selections.hasOwnProperty(optName) && this.selections[optName]) {
          visualType = this.selections[optName];
          console.log('[POD Wizard] _requiredSlotCount: found visual type, optionName =', optName, 'value =', visualType);
          break;
        }
      }
      // 如果配置的名称都没匹配上，fallback 到自动检测包含 视觉/visual/style/effect 的 key
      if (!visualType) {
        console.log('[POD Wizard] _requiredSlotCount: no config match, trying auto-detect...');
        for (var k in this.selections) {
          if (/视觉|visual|style|effect/i.test(k)) {
            visualType = this.selections[k];
            console.log('[POD Wizard] _requiredSlotCount: auto-detected visual type key =', k, 'value =', visualType);
            break;
          }
        }
      }
    }
    if (visualType && Object.prototype.hasOwnProperty.call(map, visualType)) {
      var mapping = map[visualType];
      // 兼容旧的数字格式和新的对象格式 { count: 2, hint: "..." }
      var n = (typeof mapping === 'object' && mapping !== null) ? Number(mapping.count) : Number(mapping);
      console.log('[POD Wizard] _requiredSlotCount: found mapping, returning', n);
      if (isFinite(n) && n >= 1) return n;
    }
    var fallback = this.meta.slotsMax || this.meta.slots || 1;
    console.log('[POD Wizard] _requiredSlotCount: fallback to', fallback);
    return fallback;
  };

  /* 关键修复 (P2-8): 检查未填写的必选 option, 用于在 Add to Cart 时提示用户
   * 遍历所有 options, 检查 selections 是否都有值 */
  PodWizard.prototype._missingRequiredSelections = function () {
    var missing = [];
    if (!this.options || !this.selections) return missing;
    for (var i = 0; i < this.options.length; i++) {
      var opt = this.options[i];
      var val = this.selections[opt.name];
      if (!val || val === '') {
        missing.push(opt.name);
      }
    }
    return missing;
  };

  /* ------------------------------------------------------------------
   * _applyActiveSlotCount — 把多余的 slot 隐藏 / 必需的 slot 显示
   *   切换选项时调用，不重建 DOM（避免上传中的进度条被打断）
   * ------------------------------------------------------------------ */
  PodWizard.prototype._applyActiveSlotCount = function () {
    var required = this._requiredSlotCount();
    var container = this.root.querySelector('[data-pod-upload-slots]');
    if (!container) return;

    /* 更新顶部提示文案 */
    var hint = this.root.querySelector('[data-pod-slots-hint]');
    if (hint) hint.textContent = String(required);

    /* --- 新增：动态提取并渲染自定义提示文案 --- */
    var dynamicHintEl = this.root.querySelector('[data-pod-dynamic-hint]');
    if (dynamicHintEl) {
      var activeVisualType = null;
      var activeOptionNames = (this.config && this.config.visualTypeOptionNames) || ['视觉类型'];
      if (this.selections) {
        for (var idx = 0; idx < activeOptionNames.length; idx++) {
          if (this.selections[activeOptionNames[idx]]) {
            activeVisualType = this.selections[activeOptionNames[idx]];
            break;
          }
        }
        if (!activeVisualType) {
          for (var key in this.selections) {
            if (/视觉|visual|style|effect/i.test(key)) {
              activeVisualType = this.selections[key];
              break;
            }
          }
        }
      }
      var configMap = (this.config && this.config.variantUploadSlots) || {};
      var customHint = '';
      if (activeVisualType && Object.prototype.hasOwnProperty.call(configMap, activeVisualType)) {
        var currentMapping = configMap[activeVisualType];
        if (typeof currentMapping === 'object' && currentMapping !== null && currentMapping.hint) {
          customHint = currentMapping.hint;
        }
      }
      
      if (customHint) {
        dynamicHintEl.innerHTML = customHint;
        dynamicHintEl.style.display = 'block';
      } else {
        dynamicHintEl.innerHTML = '';
        dynamicHintEl.style.display = 'none';
      }
    }
    /* ------------------------------------------ */

    /* 关键修复 (P2-9): 切到更少 slot 数时, 清理掉多余 slot 的上传数据
     * 旧逻辑只 hide slot, 不清 this.slots[i] 数据 → 用户切回更多 slot 时看到旧文件
     * 新逻辑: 如果 active 数变小, 删除 this.slots[idx] (>= required) 的临时数据
     *
     * 注意: 只清 this.slots + R2 上传 (request 到 /upload DELETE), 不影响隐藏属性的 Image URLs
     * 因为隐藏属性是从 this.slots 的 done 状态读取的. */
    if (this.slots && this.slots.length > required) {
      for (var j = this.slots.length - 1; j >= required; j--) {
        var extraSlot = this.slots[j];
        if (extraSlot && (extraSlot.key || extraSlot.publicUrl)) {
          // 异步调 R2 delete (后台清理, 不阻塞 UI)
          this._cleanupSlotUpload(extraSlot).catch(function (e) {
            console.warn('[POD Wizard] Failed to cleanup slot upload:', e.message);
          });
        }
        this.slots.splice(j, 1);
      }
    }

    /* 给每个 slot 标激活/隐藏 */
    var slotEls = container.querySelectorAll('.pod-upload-slot');
    for (var i = 0; i < slotEls.length; i++) {
      var el = slotEls[i];
      if (i < required) {
        el.style.display = '';
        el.removeAttribute('hidden');
        el.dataset.slotActive = 'true';
      } else {
        el.style.display = 'none';
        el.setAttribute('hidden', '');
        el.dataset.slotActive = 'false';
      }
    }

    /* 关键：把 CSS 变量 --pod-slot-cols 设成「当前激活的 slot 数」 */
    var cols = Math.min(required, 3);
    container.style.setProperty('--pod-slot-cols', String(cols));

    /* 重新渲染 hidden properties 反映当前激活的 slot 数 */
    this._renderHiddenProperties();
  };

  /* 关键修复 (P2-9): 清理某个 slot 在 R2 的上传文件
   * 如果 slot 有 key (即 R2 key), 调 /upload DELETE 删除 */
  PodWizard.prototype._cleanupSlotUpload = function (slot) {
    if (!slot || !slot.key) return Promise.resolve();
    var endpoint = (this.config.uploadEndpoint || '').replace(/\/+$/, '');
    if (!endpoint) return Promise.resolve();

    return fetch(endpoint + '/upload?key=' + encodeURIComponent(slot.key), {
      method: 'DELETE',
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) console.warn('[POD Wizard] R2 delete returned', r.status);
      return r;
    }).catch(function (e) {
      // 非关键错误: R2 清理失败不影响 UI, 后续 cron TEMP cleanup 会兜底
      console.warn('[POD Wizard] R2 delete network error (will be cleaned by cron):', e.message);
    });
  };

  /* ------------------------------------------------------------------
   * _restoreFromR2 — 页面加载时拉取 R2 中该 sessionId 的临时文件并回填到 slots
   *   场景：用户上次上传了图但没下单、关了页面/刷新页面/换天再来
   *        localStorage 里 sessionId 还在 → 找 R2 看有没有遗留文件 → 有就回填
   *   注意：必须在 renderUploadSlots 之后调用，slots 数组已存在
   * ------------------------------------------------------------------ */
  PodWizard.prototype._restoreFromR2 = function () {
    var self = this;
    if (!this.config.uploadEndpoint) return;
    if (!this.uploader || !this.uploader.sessionId) return;

    /* uploadEndpoint 配置形如 https://podw-upload.xxx.workers.dev/upload
       去掉末尾的 /upload → 拼到根路径 → 再加 /list?sessionId=...
       这样无论 uploadEndpoint 是带 /upload 还是不带，都能正确路由 */
    var baseUrl = this.config.uploadEndpoint.replace(/\/upload\/?$/, '');
    var listUrl = baseUrl + '/list?sessionId=' + encodeURIComponent(this.uploader.sessionId);

    fetch(listUrl)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data || !data.files || data.files.length === 0) return;

        /* 只回填到「当前激活的 slot 数」，不是所有 slot。
           例如：用户切到 3变（需要 3 张），就只回填前 3 张。
           多余的 R2 文件保留在 R2 不动，下次切回 2变 时还能用。 */
        var required = self._requiredSlotCount();
        var max = Math.min(data.files.length, required, self.slots.length);

        /* 调试日志 */
        console.log('[POD Wizard] _restoreFromR2: R2 returns', data.files.length,
                    'files, required=' + required + ', self.slots.length=' + self.slots.length);
        if (data.files.length > max) {
          console.warn('[POD Wizard] R2 里有 ' + data.files.length + ' 张图，' +
                      '但当前变体只需要 ' + required + ' 张，已回填前 ' + max + ' 张。' +
                      '切换变体时会重新回填。');
        }

        for (var idx = 0; idx < max; idx++) {
          (function (i) {
            var file = data.files[i];
            var slot = self.slots[i];
            if (!slot) return;

            /* 关键修复 (P1-1): 把 previewUrl 的 var 声明提前到使用前.
               原代码在 line 1471 才声明, 但 line 1463 已经用了, 由于 var 提升,
               这里赋给 slot.publicUrl 的会是 undefined → 刷新页面后所有图片破图.
               现在: 先计算 previewUrl → 再赋值给 slot.publicUrl → 再用同样的值设 img.src */
            var previewUrl = (file.cdnUrl || '') + '?cb=' + Date.now();

            slot.key = file.key;
            slot.sessionId = data.sessionId;
            /* 存带 cb 的 URL，既打破缓存也避免刷新后 URL 被浏览器覆盖 */
            slot.publicUrl = previewUrl;
            slot.status = 'done';

            var slotEl = self.root.querySelector('.pod-upload-slot[data-slot-index="' + i + '"]');
            if (!slotEl) return;

            var previewImg = slotEl.querySelector('[data-pod-slot-preview]');
            if (previewImg) previewImg.src = previewUrl;

            slotEl.querySelector('.pod-upload-slot__preview').classList.remove('is-hidden');
            slotEl.querySelector('.pod-upload-slot__preview').removeAttribute('hidden');
            slotEl.querySelector('.pod-upload-slot__prompt').classList.add('is-hidden');
            slotEl.querySelector('.pod-upload-slot__prompt').setAttribute('hidden', '');
            var rmBtn = slotEl.querySelector('[data-pod-slot-remove]');
            if (rmBtn) {
              rmBtn.classList.add('is-visible');
            }
            /* 关键：设 status='done'，CSS 就会自动隐藏 progress 容器
               解决恢复图片还显示 0% 进度条的 bug */
            slotEl.dataset.slotStatus = 'done';
            /* 重置 --pod-progress 为 100%（避免残留） */
            slotEl.querySelector('[data-pod-slot-progress-bar]').style.setProperty('--pod-progress', '100%');
            var progText = slotEl.querySelector('[data-pod-slot-progress-text]');
            if (progText) progText.textContent = '100%';
          })(idx);
        }

        /* 关键修复：同步隐藏 properties，并立刻刷新"加购按钮"的状态 */
        if (typeof self._syncHiddenProperties === 'function') {
          self._syncHiddenProperties();
        }
        if (typeof self.refreshSubmitState === 'function') {
          self.refreshSubmitState();
        }
      })
      .catch(function (e) {
        console.warn('[POD Wizard] _restoreFromR2 failed:', e && e.message ? e.message : e);
      });
  };

  PodWizard.prototype._buildSlot = function (index) {
    var slot = document.createElement('div');
    slot.className = 'pod-upload-slot';
    slot.dataset.slotIndex = String(index);
    slot.dataset.slotStatus = 'idle';

    slot.innerHTML = `
      <div class="pod-upload-slot__head">
        <span class="pod-upload-slot__title">Image ${index + 1}</span>
        <button type="button" class="pod-upload-slot__remove" data-pod-slot-remove data-slot-index="${index}" aria-label="Remove image ${index + 1}" hidden>×</button>
      </div>
      <div class="pod-upload-slot__dropzone" data-pod-dropzone="${index}" tabindex="0" role="button" aria-label="Click or drop image ${index + 1}">
        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif" hidden data-pod-file-input data-slot-index="${index}">
        <div class="pod-upload-slot__prompt">
          <span class="pod-upload-slot__icon" aria-hidden="true">+</span>
          <span class="pod-upload-slot__hint">Click or drop image</span>
        </div>
        <div class="pod-upload-slot__preview" hidden>
          <img alt="" data-pod-slot-preview>
          <div class="pod-upload-slot__progress" data-pod-slot-progress hidden>
            <div class="pod-upload-slot__progress-bar" data-pod-slot-progress-bar></div>
            <span class="pod-upload-slot__progress-text" data-pod-slot-progress-text>0%</span>
          </div>
        </div>
      </div>
    `;
    var self = this;
    var dropzone = slot.querySelector('[data-pod-dropzone]');
    dropzone.addEventListener('click', function (e) {
      if (e.target.closest('[data-pod-slot-remove]')) return;
      slot.querySelector('[data-pod-file-input]').click();
    });
    dropzone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        slot.querySelector('[data-pod-file-input]').click();
      }
    });
    return slot;
  };

  PodWizard.prototype.handleFileSelect = function (slotIndex, file) {
    var self = this;
    var slot = this.slots[slotIndex];
    if (!slot) return;
    var self = this;

    /* DSN-9 修复: HEIC/HEIF 自动转换 + 严格白名单
     * 流程:
     *   1) 检测 HEIC → 调 _maybeConvertHeic, 把 file 转为 JPEG
     *   2) 转换成功 → 递归 handleFileSelect, 此时 file 是 JPEG, 会走下面的白名单逻辑
     *   3) 转换失败 → UI 显示明确错误
     * 之前 (P2-10): 任何 HEIC 都被拒掉, 用户必须手动转 — 体验差
     * 现在: HEIC 自动转 JPEG, 用户零负担 */
    if (self._isHeicFile(file)) {
      if (self.onConvertStart) self.onConvertStart(file.name);
      self._maybeConvertHeic(file).then(function (convertedFile) {
        if (self.onConvertEnd) self.onConvertEnd();
        // 递归调本函数, 此时 file 是 JPEG, 会走下面的白名单逻辑
        self.handleFileSelect(slotIndex, convertedFile);
      }).catch(function (convErr) {
        if (self.onConvertEnd) self.onConvertEnd();
        self.showUploadError(
          'iPhone HEIC/HEIF photo could not be converted. ' +
          'Please open the photo in your iPhone Photos app, tap Share → Save as JPEG, then upload again. ' +
          '(Error: ' + (convErr.message || convErr) + ')'
        );
      });
      return;
    }

    // 文件类型校验（HEIC 已被上面拦截, 到这里的是 JPEG/PNG/WebP/GIF）
    /* 关键修复 (P2-10): 严格白名单, 不再接受 image/* 通配
     * 旧: /^image\// → 接受 HEIC/HEIF (iPhone 默认) → 上传后设计师看不见
     * 新: 白名单 JPEG/PNG/WebP/GIF (浏览器可显示 + 设计师可查看) */
    var allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    var allowedExts = /\.(jpe?g|png|webp|gif)$/i;
    if (allowedTypes.indexOf(file.type) < 0 && !allowedExts.test(file.name || '')) {
      this.showUploadError('File must be JPEG, PNG, WebP, or GIF. iPhone HEIC format is not supported — please convert to JPEG first.');
      return;
    }
    var maxBytes = (this.config.maxFileMB || 10) * 1024 * 1024;
    if (file.size > maxBytes) {
      this.showUploadError('File exceeds ' + (this.config.maxFileMB || 10) + ' MB.');
      return;
    }

    slot.file = file;
    slot.status = 'uploading';
    slot.progress = 0;

    /* 先把 status='uploading' 同步设上 → 让 progress 容器从默认 hidden 变 flex 显示
       否则 reader.onload 异步触发期间，进度回调先跑，bar 还没 display，width 设了看不到 */
    var slotElEarly = self.root.querySelector('.pod-upload-slot[data-slot-index="' + slotIndex + '"]');
    if (slotElEarly) {
      slotElEarly.dataset.slotStatus = 'uploading';
      var pb = slotElEarly.querySelector('[data-pod-slot-progress-bar]');
      if (pb) pb.style.setProperty('--pod-progress', '0%');
    }

    // 本地预览
    var reader = new FileReader();
    reader.onload = function () {
      slot.previewUrl = reader.result;
      var slotEl = self.root.querySelector('.pod-upload-slot[data-slot-index="' + slotIndex + '"]');
      if (!slotEl) return;
      slotEl.querySelector('[data-pod-slot-preview]').src = slot.previewUrl;
      slotEl.querySelector('.pod-upload-slot__preview').classList.remove('is-hidden');
      slotEl.querySelector('.pod-upload-slot__preview').removeAttribute('hidden');
      slotEl.querySelector('.pod-upload-slot__prompt').classList.add('is-hidden');
      slotEl.querySelector('.pod-upload-slot__prompt').setAttribute('hidden', '');
      /* progress 容器不再需要手动类控制 — slotStatus=uploading 已经让它显示 */
      slotEl.dataset.slotStatus = 'uploading';
      /* 注入 shimmer 扫光动画元素 — 纯 CSS，100% 可靠 */
      var bar = slotEl.querySelector('[data-pod-slot-progress-bar]');
      if (bar && !bar.querySelector('.pod-upload-slot__progress-bar__shimmer')) {
        var shim = document.createElement('div');
        shim.className = 'pod-upload-slot__progress-bar__shimmer';
        bar.appendChild(shim);
      }
    };
    reader.readAsDataURL(file);

    this.uploader.upload(file, {
      onProgress: function (pct) {
        slot.progress = pct;
        var slotEl = self.root.querySelector('.pod-upload-slot[data-slot-index="' + slotIndex + '"]');
        if (!slotEl) return;
        var txt = slotEl.querySelector('[data-pod-slot-progress-text]');
        if (txt) txt.textContent = pct + '%';
      },
      onError: function (err) {
        slot.status = 'error';
        var slotEl = self.root.querySelector('.pod-upload-slot[data-slot-index="' + slotIndex + '"]');
        if (slotEl) slotEl.dataset.slotStatus = 'error';
        self.showUploadError(err.message || 'Upload failed');
      }
    });
  };

  PodWizard.prototype._onUploadComplete = function (payload) {
    // 找到当前正在上传的槽
    var slot = this.slots.find(function (s) { return s.status === 'uploading'; });
    if (!slot) return;
    slot.publicUrl = payload.publicUrl;
    slot.key = payload.key || '';
    slot.sessionId = payload.sessionId || getOrCreateSessionId();
    slot.status = 'done';

    var slotIdx = this.slots.indexOf(slot);
    var slotEl = this.root.querySelector('.pod-upload-slot[data-slot-index="' + slotIdx + '"]');
    if (slotEl) {
      slotEl.dataset.slotStatus = 'done';
      slotEl.querySelector('[data-pod-slot-progress]').classList.add('is-hidden');
      /* 有图片（publicUrl 非空）时才显示删除按钮 */
      if (payload.publicUrl) {
        var rmBtn = slotEl.querySelector('[data-pod-slot-remove]');
        if (rmBtn) {
          rmBtn.classList.add('is-visible');
        }
      }
    }

    // 同步隐藏 properties（包含 sessionId）
    this._syncHiddenProperties();
    this.showUploadError(null);
    this.refreshSubmitState(); // Step 5 submit button
  };

  /**
   * 删除上传槽
   * - 真实上传（非 mock）：调 Worker DELETE /upload?key= 删 R2 文件
   * - mock 模式：只清本地 state
   */
  PodWizard.prototype.removeSlot = async function (slotIndex) {
    var slot = this.slots[slotIndex];
    if (!slot) return;

    // 如果是真实上传的（有 key 且非 mock），先调 Worker 删 R2
    if (slot.key && !slot.key.startsWith('mock-') && this.config.uploadEndpoint) {
      try {
        var encodedKey = encodeURIComponent(slot.key);
        /* uploadEndpoint 形如 https://podw-upload.xxx.workers.dev（无 /upload 后缀），
           DELETE /upload?key= 才是正确的删除路由 */
        var baseUrl = this.config.uploadEndpoint.replace(/\/$/, '');
        var deleteUrl = baseUrl + '/upload?key=' + encodedKey;
        var resp = await fetch(deleteUrl, { method: 'DELETE' });
        if (!resp.ok) {
          var errBody = await resp.json().catch(function() { return {}; });
          console.warn('[POD Wizard] R2 delete failed:', errBody.error || resp.status);
        } else {
          console.log('[POD Wizard] R2 file deleted:', slot.key);
        }
      } catch (e) {
        // 网络错误不影响本地 UI 清除
        console.warn('[POD Wizard] R2 delete error:', e.message);
      }
    }

    // 清除本地 state
    slot.file = null;
    slot.previewUrl = null;
    slot.publicUrl = null;
    slot.key = null;
    slot.sessionId = null;
    slot.status = 'idle';
    slot.progress = 0;

    var slotEl = this.root.querySelector('.pod-upload-slot[data-slot-index="' + slotIndex + '"]');
    if (!slotEl) return;
    slotEl.querySelector('[data-pod-slot-preview]').src = '';
    slotEl.querySelector('.pod-upload-slot__preview').classList.add('is-hidden');
    slotEl.querySelector('.pod-upload-slot__preview').setAttribute('hidden', '');
    slotEl.querySelector('.pod-upload-slot__prompt').classList.remove('is-hidden');
    slotEl.querySelector('.pod-upload-slot__prompt').removeAttribute('hidden');
    var removeBtn = slotEl.querySelector('[data-pod-slot-remove]');
    if (removeBtn) removeBtn.classList.remove('is-visible');
    /* 清进度条 inline gradient + shimmer 元素 */
    var bar = slotEl.querySelector('[data-pod-slot-progress-bar]');
    if (bar) {
      var shim = bar.querySelector('.pod-upload-slot__progress-bar__shimmer');
      if (shim) shim.remove();
      bar.style.background = '';
    }
    var txt = slotEl.querySelector('[data-pod-slot-progress-text]');
    if (txt) txt.textContent = '0%';
    slotEl.dataset.slotStatus = 'idle';

    this._syncHiddenProperties();
    this.refreshSubmitState(); // Step 5 submit button
  };

  PodWizard.prototype._renderHiddenProperties = function () {
    /* 关键修复 (P1-2): 把 hidden properties input 放到最近的 <form> 里 (Shopify cart form),
       而不是 wizard 内部. 否则表单提交时, 这些 inputs 不在 form 内, properties 不会传到订单.

       流程:
       1) 找最近的祖先 <form> (Shopify cart form: <form action="/cart/add">)
       2) 找不到的话, 回退到 wizard section root (wizard 自包含, 不太理想但至少不会丢)
       3) holder 用 [data-pod-hidden-properties] 标记, 避免重复 */
    var form = this.root.closest && this.root.closest('form');
    var target = form || this.root;
    var holder = target.querySelector('[data-pod-hidden-properties]');
    if (!holder) {
      holder = document.createElement('div');
      holder.className = 'pod-hidden-properties';
      holder.setAttribute('aria-hidden', 'true');
      holder.dataset.podHiddenProperties = '';
      // 插入到目标 form 内靠后的位置
      target.appendChild(holder);
    }
    holder.innerHTML = '';
  };

  PodWizard.prototype._syncHiddenProperties = function () {
    /* 关键修复 (P1-2): 找到最近的 <form> (Shopify cart form) 来放 inputs.
       注意: 与 _renderHiddenProperties 保持一致的查找逻辑. */
    var form = this.root.closest && this.root.closest('form');
    var target = form || this.root;
    var holder = target.querySelector('[data-pod-hidden-properties]');
    if (!holder) {
      // holder 还没创建, 调 _renderHiddenProperties 来创建
      this._renderHiddenProperties();
      holder = target.querySelector('[data-pod-hidden-properties]');
      if (!holder) return;
    }
    holder.innerHTML = '';

    var doneSlots = this.slots.filter(function (s) { return s.status === 'done' && s.publicUrl; });
    var sessionId = doneSlots.length > 0 && doneSlots[0].sessionId
      ? doneSlots[0].sessionId
      : (this.uploader ? this.uploader.sessionId : getOrCreateSessionId());

    // Upload mode: Submitted | SendLater | (empty)
    // 注意: 这是最终提交进 cart 的 upload 模式。
    //   - 如果用户已传图（不管几张），设 Submitted（已下单时上传完成）
    //   - SendLater 则保留
    // "是否传够 N 张"在订单页由 _required_slot_count + 已上传数校验
    var uploadMode = '';
    if (doneSlots.length > 0) {
      uploadMode = 'Submitted';
    } else if (this.sendLater) {
      uploadMode = 'SendLater';
    }

    // 该订单 / 售后页需要上传几张图（数据源：产品的 metafield pod.variant_upload_slots + 用户选的"视觉类型"）
    // 订单页没有 product 对象，没法反查 metafield，所以由 Wizard 写入最稳
    var requiredSlotCount = this._requiredSlotCount();

    /* 关键修复: 支持多个名称（逗号分隔），与 _requiredSlotCount 一致的查找逻辑. */
    var visualType = '';
    var optionNames = (this.config && this.config.visualTypeOptionNames) || ['视觉类型'];
    if (this.selections) {
      // 遍历配置的名称列表，找第一个产品中存在的选项
      for (var i = 0; i < optionNames.length; i++) {
        var optName = optionNames[i];
        if (this.selections.hasOwnProperty(optName) && this.selections[optName]) {
          visualType = this.selections[optName];
          break;
        }
      }
      // 如果配置的名称都没匹配上，fallback 到自动检测
      if (!visualType) {
        for (var k in this.selections) {
          if (/视觉|visual|style|effect/i.test(k)) {
            visualType = this.selections[k];
            break;
          }
        }
      }
    }

    // 内部开关（下划线前缀，Shopify 前端隐藏后台可见）
    addInput(holder, '_pod_template', 'pod');
    addInput(holder, '_upload_mode', uploadMode);
    addInput(holder, '_required_slot_count', String(requiredSlotCount));
    addInput(holder, '_visual_type', visualType);
    // P2-A 修复 (2026-07-22): _session_id 是死代码, 注释掉.
    //   实际下游读的是 _pod_session_id (由 wizard sessionId 变量统一传入).
    //   全仓 grep '_session_id' 精确匹配: 只有这里一处写, 0 处读.
    // addInput(holder, '_session_id', sessionId);  // P2-A: 死代码, 注释

    doneSlots.forEach(function (s, idx) {
      addInput(holder, 'Image ' + (idx + 1), s.publicUrl);
    });
  };

  function addInput(holder, name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[' + name + ']';
    input.value = value || '';
    input.dataset.podProp = name;
    holder.appendChild(input);
  }

  PodWizard.prototype.showUploadError = function (msg) {
    var el = this.root.querySelector('[data-pod-upload-error]');
    if (!el) return;
    if (!msg) { el.classList.add('is-hidden'); el.textContent = ''; return; }
    el.textContent = msg;
    el.classList.remove('is-hidden');
  };

  /* DSN-9: HEIC 转换进度提示 (与错误共用一行, 用户只看到一条信息) */
  PodWizard.prototype.onConvertStart = function (filename) {
    var el = this.root.querySelector('[data-pod-upload-error]');
    if (!el) return;
    el.textContent = 'Converting HEIC → JPEG: ' + filename + ' … (this may take a few seconds)';
    el.classList.remove('is-hidden');
  };

  PodWizard.prototype.onConvertEnd = function () {
    var el = this.root.querySelector('[data-pod-upload-error]');
    if (!el) return;
    /* 转换完成后, 如果没有错误, 隐藏提示; 后续真实上传会用 slot 的 progress bar 反馈 */
    el.textContent = '';
    el.classList.add('is-hidden');
  };

  /* ------------------------------------------------------------------
   * Phase 4: Review panel rendering
   * ------------------------------------------------------------------ */

  PodWizard.prototype.renderReview = function () {
    var selContainer = this.root.querySelector('[data-pod-review-selections]');
    var uplContainer = this.root.querySelector('[data-pod-review-uploads]');
    if (!selContainer) return;
    selContainer.innerHTML = '';
    uplContainer.innerHTML = '';

    // 选项摘要
    var self = this;
    Object.keys(this.selections).forEach(function (name) {
      var val = self.selections[name];
      if (!val) return;
      var row = document.createElement('div');
      row.className = 'pod-review__row';
      row.innerHTML =
        '<span class="pod-review__label">' + escHtml(name) + '</span>' +
        '<span class="pod-review__value">' + escHtml(val) + '</span>';
      selContainer.appendChild(row);
    });

    // 如果没有选项，显示默认文案
    if (Object.keys(this.selections).length === 0) {
      var noSel = document.createElement('div');
      noSel.className = 'pod-review__row';
      noSel.innerHTML = '<span class="pod-review__label">Options</span><span class="pod-review__value pod-review__value--muted">Not selected</span>';
      selContainer.appendChild(noSel);
    }

    // 上传摘要
    var doneSlots = this.slots.filter(function (s) { return s.status === 'done' && s.publicUrl; });
    if (doneSlots.length > 0) {
      doneSlots.forEach(function (s) {
        if (/^data:image/.test(s.publicUrl)) {
          // 本地预览（mock 模式）
          var img = document.createElement('img');
          img.className = 'pod-review__thumb';
          img.src = s.publicUrl;
          img.alt = 'Uploaded image';
          uplContainer.appendChild(img);
        } else {
          // 真实 CDN URL
          var a = document.createElement('a');
          a.href = s.publicUrl;
          a.target = '_blank';
          a.rel = 'noopener';
          a.title = 'View uploaded image';
          var img2 = document.createElement('img');
          img2.className = 'pod-review__thumb';
          img2.src = s.publicUrl;
          img2.alt = 'Uploaded image';
          a.appendChild(img2);
          uplContainer.appendChild(a);
        }
      });
    } else {
      var noImg = document.createElement('span');
      noImg.className = 'pod-review__label';
      noImg.textContent = 'No images uploaded yet';
      uplContainer.appendChild(noImg);
    }
  };

  /* ------------------------------------------------------------------
   * Phase 4: Submit button state
   * ------------------------------------------------------------------ */

  PodWizard.prototype.refreshSubmitState = function () {
    // Add to Cart button is on the last step
    var lastStep = this.steps[this.steps.length - 1];
    var submitBtn = lastStep ? lastStep.querySelector('[data-pod-next]') : null;
    if (!submitBtn) return;

    // Crown & Paw style: Use first variant as default if none selected
    var variant = this.currentVariant;
    if (!variant && this.variants.length > 0) {
      variant = this.variants[0];
    }

    var canSubmit = variant && variant.available !== false;
    // P2-B 修复 (2026-07-22): required_count 校验
    //   原 line 2128 只判 "至少 1 张图" → 视觉类型要求 3 张但只传了 1 张时, 按钮仍可点.
    //   现在: 传够 _requiredSlotCount 张图 OR 勾选 Send Later, 才允许提交.
    var doneSlotsCount = this.slots.filter(function (s) { return s.status === 'done' && s.publicUrl; }).length;
    var requiredCount = this._requiredSlotCount ? this._requiredSlotCount() : 1;
    var hasEnoughImages = !this.uploadEnabled || doneSlotsCount >= requiredCount;
    canSubmit = canSubmit && (hasEnoughImages || this.sendLater);
    submitBtn.disabled = !canSubmit;
    submitBtn.setAttribute('aria-disabled', canSubmit ? 'false' : 'true');
  };

  /* ------------------------------------------------------------------
   * Phase 4: Add-to-Cart + Storefront API + /rename
   * ------------------------------------------------------------------ */

  /**
   * 获取 Storefront API Access Token
   * 优先从 data 属性读取（section 设置），其次从 window.POD_CONFIG 读取
   */
  PodWizard.prototype._getStorefrontToken = function () {
    var token = this.root.dataset.podStorefrontToken || '';
    if (!token && window.POD_CONFIG && window.POD_CONFIG[this.sId]) {
      token = window.POD_CONFIG[this.sId].storefrontToken || '';
    }
    return token;
  };

  /**
   * 获取 Storefront API Endpoint
   * 优先从 data 属性读取，其次从 window.POD_CONFIG 读取，最后用默认域名
   */
  PodWizard.prototype._getStorefrontEndpoint = function () {
    var endpoint = this.root.dataset.podStorefrontEndpoint || '';
    if (!endpoint && window.POD_CONFIG && window.POD_CONFIG[this.sId]) {
      endpoint = window.POD_CONFIG[this.sId].storefrontEndpoint || '';
    }
    // 只返回用户明确配置的 endpoint，不自动构建
    // 这样确保默认使用 AJAX cart（加入购物车 → 购物车页面）
    return endpoint;
  };

  /**
   * 用 Storefront API 创建 Checkout（Cart）
   * 这样可以立即获得 orderId，不需要等付款
   */
  PodWizard.prototype._createCheckout = function (variantId, propsMap, sessionId, qty) {
    var self = this;
    qty = qty || 1;
    var token = this._getStorefrontToken();
    var endpoint = this._getStorefrontEndpoint();

    // 如果没有配置 endpoint，直接回退到 AJAX cart
    if (!endpoint) {
      console.warn('[POD Wizard] No Storefront endpoint configured. Falling back to AJAX cart.');
      return Promise.reject({ useAjax: true });
    }

    // Token 是可选的（Shopify 支持 tokenless cart 操作）
    // 如果没有 token，就用 tokenless 方式调用
    var headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['X-Shopify-Storefront-Access-Token'] = token;
    }

    // 把 properties 格式化为 Storefront API 的 LineItemCustomization 格式
    var customAttributes = Object.keys(propsMap).map(function (key) {
      return { key: key, value: propsMap[key] };
    });

    // 获取选中的 Extras
    var selectedExtras = (window.POD_EXTRAS_STATE && window.POD_EXTRAS_STATE.selectedExtras) || [];

    // 构建所有 line items
    var lines = [{
      merchandiseId: 'gid://shopify/ProductVariant/' + variantId,
      quantity: qty,
      attributes: customAttributes
    }];
    // 主商品添加 sessionId 到 attributes，等 webhook 触发迁移
    if (sessionId) {
      lines[0].attributes.push({ key: '_pod_session_id', value: sessionId });
    }
    selectedExtras.forEach(function (extra) {
      if (extra.variantId) {
        var extraAttributes = [{ key: '_is_extra', value: 'true' }];
        if (sessionId) {
          extraAttributes.push({ key: '_pod_session_id', value: sessionId });
        }
        lines.push({
          merchandiseId: 'gid://shopify/ProductVariant/' + extra.variantId,
          quantity: qty,
          attributes: extraAttributes
        });
      }
    });

    var mutation = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
            totalQuantity
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    var variables = {
      input: {
        lines: lines
      }
    };

    return fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ query: mutation, variables: variables })
    }).then(function (r) { return r.json(); })
      .then(function (result) {
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }
        var cart = result.data && result.data.cartCreate && result.data.cartCreate.cart;
        if (!cart) {
          var err = result.data && result.data.cartCreate && result.data.cartCreate.userErrors;
          if (err && err.length > 0) {
            throw new Error(err[0].message);
          }
          throw new Error('Failed to create checkout');
        }

        // 关键修复 (P0-3): Storefront API 把 _pod_session_id 放在 attributes 里,
        // 但 Shopify webhook 读 item.properties, 拿不到 sessionId → rename 永不触发.
        // 解决方案: checkout 创建成功后, 主动调 /rename, 用 cart.id 作为临时 ID.
        if (sessionId && self._callRename) {
          try {
            var tempOrderId = cart.id || ('cart-' + Date.now());
            /* DSN-3 修复: Worker 端 /rename 强制要求 YYYY-MM-DD date, null/空字符串都会 400.
             *   upload-worker.js:325 if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) → 400
             * 这里传 ISO 今日日期, 与 line 2257 (Storefront 成功回调) 保持一致. */
            var todayDate = new Date().toISOString().slice(0, 10);
            self._callRename(sessionId, tempOrderId, todayDate).catch(function (renameErr) {
              console.warn('[POD Wizard] /rename after Storefront checkout failed (will rely on webhook fallback):', renameErr);
            });
          } catch (e) {
            console.warn('[POD Wizard] _callRename sync error:', e);
          }
        }

        return cart;
      });
  };

  /**
   * 调用 Worker /rename 接口
   * 在订单创建后立即调用，把 sessionId 的文件索引写入 _index/
   *
   * Worker 端 verify (upload-worker.js:339): `expectedPayload = ${sessionId}:${orderId}:${date}`
   * HMAC_SECRET 强制要求 X-Signature, 没 SECRET 500, 没签名 401.
   */
  PodWizard.prototype._callRename = function (sessionId, orderId, date) {
    var self = this;
    var workerUrl = (this.config.uploadEndpoint || window.POD_UPLOAD_WORKER_ENDPOINT || '').replace(/\/upload\/?$/, '') + '/rename';
    var secret = (window.POD_CONFIG && this.sId && window.POD_CONFIG[this.sId] && window.POD_CONFIG[this.sId].hmacSecret) || '';
    if (!secret) {
      console.warn('[POD Wizard] /rename skipped — pod_hmac_secret not configured in theme settings');
      return Promise.resolve({ success: false, skipped: 'no_hmac_secret' });
    }
    var payload = sessionId + ':' + orderId + ':' + date;
    return window.POD_HMAC.signedFetchJSON(workerUrl, {
      sessionId: sessionId,
      orderId: orderId,
      date: date
    }, secret, payload, 'X-Signature').then(function (result) {
      if (!result.success) {
        console.warn('[POD Wizard] /rename failed:', result.error || result.message);
      } else {
        console.log('[POD Wizard] /rename success:', result);
      }
      return result;
    }).catch(function (err) {
      console.warn('[POD Wizard] /rename error:', err);
      return { success: false, error: err.message };
    });
  };

  /**
   * 从 checkoutUrl 中提取 orderId
   * 格式: https://shop.com/checkout?cart_token=xxx 或直接带 order_id
   * Storefront API 返回的 checkoutUrl 通常包含 cart_token，我们用它作为 orderId
   */
  PodWizard.prototype._extractOrderId = function (checkoutUrl) {
    if (!checkoutUrl) return null;
    var url = new URL(checkoutUrl, window.location.origin);
    var cartToken = url.searchParams.get('cart');
    if (cartToken) return cartToken;
    // fallback: 用时间戳 + 随机数作为伪 orderId
    return 'ORD-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6);
  };

  PodWizard.prototype.handleAddToCart = function () {
    var log = '=== ADD TO CART CLICKED ===\n';
    log += 'Time: ' + new Date().toISOString() + '\n';
    sessionStorage.setItem('pod_last_action', log);

    var self = this;
    var currentStep = this.steps[this.current];
    var submitBtn = currentStep ? currentStep.querySelector('[data-pod-next]') : null;
    log += 'currentStep: ' + (currentStep ? currentStep.dataset.stepType : 'null') + '\n';
    log += 'submitBtn: ' + (submitBtn ? 'found' : 'null') + '\n';
    log += 'submitBtn.disabled: ' + (submitBtn && submitBtn.disabled ? 'yes' : 'no') + '\n';
    sessionStorage.setItem('pod_last_action', log);

    /* 关键修复 (P2-8): 不再 silent fallback 到 variants[0]
     * 原来: 用户没选任何 option, 默默用第一个变体 → 拿到错误规格, 加购物车后才发现不对
     * 现在: 显式提示用户, 引导回选项步骤
     *
     * 兼容: 如果没有变体可用, 提示技术支持 (而不是默默猜). */
    if (!this.currentVariant) {
      if (!this.variants || this.variants.length === 0) {
        log += 'ERROR: No variants at all for this product\n';
        sessionStorage.setItem('pod_last_action', log);
        this.showReviewError('This product has no variants available. Please contact support.');
        return;
      }
      /* 检查 selections 是否完整: 如果不完整, 引导用户 */
      var missing = this._missingRequiredSelections();
      if (missing.length > 0) {
        log += 'ERROR: Missing required selections: ' + missing.join(', ') + '\n';
        sessionStorage.setItem('pod_last_action', log);
        this.showReviewError('Please select: ' + missing.join(', ') + ' before adding to cart.');
        // 滚动到第一个未选项
        var firstStep = this.root.querySelector('[data-step-type="option"]');
        if (firstStep) {
          var stepIdx = parseInt(firstStep.dataset.stepIndex || '0', 10);
          this.go(stepIdx);
        }
        return;
      }
      /* 所有 option 都选了, 但 findMatchingVariant 返回 null (罕见, 通常是 product 配置问题) */
      log += 'WARN: Could not find matching variant, using first available as last resort\n';
      sessionStorage.setItem('pod_last_action', log);
      this.currentVariant = this.variants[0];
      console.warn('[POD Wizard] No matching variant, defaulting to first variant', this.currentVariant);
    }
    if (submitBtn && submitBtn.disabled) {
      log += 'ERROR: Button is disabled, returning early\n';
      sessionStorage.setItem('pod_last_action', log);
      return;
    }

    /* P2-B 防御性二次校验 (2026-07-22): 即便按钮被启用 (例如 DevTools 绕过),
     * 也阻止"传不够图且没勾 Send Later"走到 checkout.
     * 主要校验在 refreshSubmitState (line 2127-2135) 已做, 这里做最后一道闸门. */
    var doneSlotsCount = this.slots.filter(function (s) { return s.status === 'done' && s.publicUrl; }).length;
    var requiredCount = this._requiredSlotCount ? this._requiredSlotCount() : 1;
    if (this.uploadEnabled && !this.sendLater && doneSlotsCount < requiredCount) {
      log += 'ERROR: P2-B required_slot_count check failed: ' + doneSlotsCount + '/' + requiredCount + '\n';
      sessionStorage.setItem('pod_last_action', log);
      this.showReviewError(
        'Please upload at least ' + requiredCount + ' photo(s) before adding to cart, ' +
        'or choose "Send Photos Later".'
      );
      var uploadStep = this.root.querySelector('[data-step-type="upload"]');
      if (uploadStep) {
        var stepIdx = parseInt(uploadStep.dataset.stepIndex || '0', 10);
        this.go(stepIdx);
      }
      return;
    }

    // 【新增】获取原生数量选择器的值
    var qtyInput = this.root.querySelector('[name="quantity"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    log += 'Proceeding to add to cart...\n';
    sessionStorage.setItem('pod_last_action', log);

    // Loading 状态
    if (submitBtn) {
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
    }
    this.showReviewError(null);

    var variantId = this.currentVariant.id;

    // 收集 hidden properties
    // 关键修复 (CRITICAL-BUG): _syncHiddenProperties 把属性写到最近的 <form> 里 (P1-2 修复),
    // 但 handleAddToCart 原来只用 this.root.querySelectorAll 搜索 wizard section 内部,
    // 找不到表单里的 hidden inputs, 导致 propsMap 总是空的 _upload_mode 读不到.
    // 正确做法: 同时搜索 this.root 及其父 form, 两者都搜.
    var propHolders = [];
    if (this.root) propHolders = Array.from(this.root.querySelectorAll('[data-pod-prop]'));
    var parentForm = this.root && this.root.closest && this.root.closest('form');
    if (parentForm) {
      propHolders = propHolders.concat(Array.from(parentForm.querySelectorAll('[data-pod-prop]')));
    }
    var propsMap = {};
    propHolders.forEach(function (input) {
      var name = input.dataset.podProp;
      if (name) propsMap[name] = input.value;
    });

    // 添加 rush 状态到 propsMap（两种路径都会使用）
    var hasRush = (window.POD_EXTRAS_STATE && window.POD_EXTRAS_STATE.hasRush) || false;
    if (hasRush) {
      propsMap['_has_rush'] = 'true';
    }

    // 获取 sessionId（从第一个已完成的 slot 或 uploader）
    var doneSlots = this.slots.filter(function (s) { return s.status === 'done' && s.publicUrl; });
    var sessionId = (doneSlots.length > 0 && doneSlots[0].sessionId)
      ? doneSlots[0].sessionId
      : (this.uploader ? this.uploader.sessionId : null);

    // 尝试用 Storefront API 创建 Checkout
    this._createCheckout(variantId, propsMap, sessionId, qty)
      .then(function (cart) {
        // 成功：获取 orderId（cart token）
        var orderId = self._extractOrderId(cart.checkoutUrl);
        console.log('[POD Wizard] Checkout created, orderId:', orderId);

        /* 关键修复 (P2-1): Storefront 路径立即调 _callRename.
         * 原代码只有 AJAX 路径不调, 而 Storefront 路径也没调 → rename 完全没触发.
         * 现在: 拿到 orderId 后立即调, webhook 作为兜底. */
        if (sessionId && orderId && self._callRename) {
          var date = new Date().toISOString().slice(0, 10);
          self._callRename(sessionId, orderId, date).catch(function (renameErr) {
            console.warn('[POD Wizard] /rename after Storefront checkout failed (will rely on webhook fallback):', renameErr);
          });
        }

        // 跳转 checkout
        window.location.href = cart.checkoutUrl || '/checkout';
      })
      .catch(function (err) {
        // 如果没有 Storefront Token，回退到 AJAX cart
        if (err.useAjax) {
          return self._addToCartAjax(variantId, propsMap, sessionId, qty);
        }
        throw err;
      })
      .catch(function (err) {
        // 错误处理
        if (submitBtn) {
          submitBtn.classList.remove('is-loading');
          submitBtn.disabled = false;
        }
        self.showReviewError(err.message || 'Something went wrong. Please try again.');
      });
  };

  /**
   * Fallback: 使用传统 AJAX Add to Cart（如果没有 Storefront Token）
   * 注意：这种方式无法立即获得 orderId，/rename 需要等付款后通过 webhook 触发
   */
PodWizard.prototype._addToCartAjax = function (variantId, propsMap, sessionId, qty) {
  var self = this;
  qty = qty || 1;
  var currentStep = this.steps[this.current];
  var submitBtn = currentStep ? currentStep.querySelector('[data-pod-submit]') : null;
  var spinner = submitBtn ? submitBtn.querySelector('[data-pod-submit-spinner]') : null;
  var btnLabel = submitBtn ? submitBtn.querySelector('[data-pod-submit-label]') : null;

  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.classList.add('is-loading');
  }
  if (spinner) spinner.removeAttribute('hidden');
  if (btnLabel) btnLabel.style.display = 'none';

  // 1. 获取选中的 Extras
  var selectedExtras = (window.POD_EXTRAS_STATE && window.POD_EXTRAS_STATE.selectedExtras) || [];

  // 2. 从真实的 DOM 中提取所有 Extras，构建完整的附加商品池
  var allExtrasPool = [];
  try {
    var extraEls = document.querySelectorAll('[data-pod-extra-item]');
    extraEls.forEach(function(el) {
      var vId = el.getAttribute('data-extra-variant-id');
      if (!vId) return;
      var vIdNum = parseInt(vId, 10);
      if (isNaN(vIdNum) || vIdNum <= 0) return;

      // 精确抓取源码定义的 headline 类和 thumb 下的图片
      var imgEl = el.querySelector('.pod-extras-item__thumb img');
      var titleEl = el.querySelector('.pod-extras-item__headline');

      // 将所有附加商品都装入池中，不再剔除已被选中的商品
      allExtrasPool.push({
        variantId: vIdNum,
        headline: titleEl ? titleEl.textContent.trim() : 'Additional Add-on',
        priceCents: parseInt(el.getAttribute('data-extra-price-cents') || '0', 10),
        originalPriceCents: parseInt(el.getAttribute('data-variant-price') || '0', 10),
        thumbnailUrl: imgEl ? imgEl.src : ''
      });
    });
  } catch (e) {
    console.error('POD: Failed to parse extras from DOM', e);
  }

  // 组装要添加到购物车的商品数组
  var itemsToAdding = [];
  var commonProps = {};
  Object.keys(propsMap).forEach(function (key) {
    if (propsMap[key]) commonProps[key] = propsMap[key];
  });

  var mainProps = Object.assign({}, commonProps);
  if (sessionId) {
    mainProps['_pod_session_id'] = sessionId;
  }

  if (allExtrasPool.length > 0) {
    mainProps['_pod_upsells'] = JSON.stringify(allExtrasPool);
  }

  itemsToAdding.push({
    id: parseInt(variantId, 10),
    quantity: qty,
    properties: mainProps
  });

  selectedExtras.forEach(function (extra) {
    var eVariantId = parseInt(extra.variantId, 10);
    if (!isNaN(eVariantId) && eVariantId !== 0) {
      // 绝对不继承 commonProps (内含用户上传图片的冗长链接)
      var extraProps = {};
      if (sessionId) {
          extraProps['_pod_session_id'] = sessionId;
      }
      extraProps['_is_extra'] = 'true';

      // 如果需要传递加急标记，单独从 commonProps 提取（以防万一）
      if (commonProps['_has_rush']) {
          extraProps['_has_rush'] = commonProps['_has_rush'];
      }

      itemsToAdding.push({
        id: eVariantId,
        quantity: qty,
        properties: extraProps
      });
    }
  });

  // 3. 获取 Dawn 抽屉需要的 sections 参数
  var cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  var sectionsToRender = [];
  if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
    sectionsToRender = cartEl.getSectionsToRender().map(function(s) { return s.id; });
  } else {
    sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];
  }

  var requestBody = {
    items: itemsToAdding,
    sections: sectionsToRender,
    sections_url: window.location.pathname
  };

  // 4. 发送严格标准的 JSON 请求
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody)
  })
  .then(function (response) {
    if (!response.ok) {
      return response.json().then(function(json) {
        throw new Error(json.description || 'Error adding to cart');
      });
    }
    return response.json();
  })
  .then(function (responseJSON) {
    // 强制清除当前批次号缓存，确保下一次加购生成全新独立的 ID，从而让 Shopify 拆分商品行
    localStorage.removeItem('pod_session_id');

    // 5. 使用 Dawn 原生方法渲染 HTML，解决空白问题
    if (cartEl && typeof cartEl.renderContents === 'function') {
      cartEl.renderContents(responseJSON);
      // 必须同步 product-form.js 第 79 行的原生逻辑：手动移除 is-empty，否则 CSS 会强制隐藏列表导致变形
      if (cartEl.classList.contains('is-empty')) {
        cartEl.classList.remove('is-empty');
      }
    } else {
      if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pod-wizard' });
      }
    }

    // 展开侧边栏
    if (cartEl && typeof cartEl.open === 'function') {
      cartEl.open();
    } else {
      window.location.href = '/cart';
    }
  })
  .catch(function (err) {
    self.showReviewError(err.message);
  })
  .finally(function () {
    setTimeout(function() {
      if (submitBtn) {
        submitBtn.classList.remove('is-loading');
        submitBtn.disabled = false;
      }
      if (spinner) spinner.setAttribute('hidden', '');
      if (btnLabel) btnLabel.style.display = '';
    }, 800);
  });
};

PodWizard.prototype.showReviewError = function (msg) {
    // Fallback: if no error element, show alert
    var el = this.root.querySelector('[data-pod-review-error]');
    if (!el) {
      if (msg) alert(msg);
      return;
    }
    if (!msg) { el.hidden = true; el.textContent = ''; return; }
    el.textContent = msg;
    el.hidden = false;
  };

  /* ------------------------------------------------------------------
   * Bootstrap
   * ------------------------------------------------------------------ */

  function init() {
    document.querySelectorAll('[data-section-type="main-pod-wizard"]').forEach(function (el) {
      if (el.__podWizardInited) return;
      el.__podWizardInited = true;
      new PodWizard(el);
    });
  }

  document.addEventListener('shopify:section:load', function (e) {
    var target = (e.target && e.target.querySelector) ? e.target.querySelector('[data-section-type="main-pod-wizard"]') : null;
    if (target && !target.__podWizardInited) {
      target.__podWizardInited = true;
      new PodWizard(target);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
